import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Select from "react-select";
import CollapseHeader from "../../../core/common/collapse-header";
import { useContext } from "react";
import { AuthContext } from "../../../context/authcontext";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { API_BASE_URL } from "../../../environment";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import PrivacySettings from "./PrivacySettings";
import TeamAllocationSettings from "./TeamAllocationSettings";
import CalendarSettings from "./CalendarSettings";
import { updateChatIntegrationStatus } from "../../../utils/chatIntegration";
import { ChatService } from "../../application/components/chat/chatService";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import Switch from "../../../components/toggleButton";
import AutoCreationRules from "./autoCreationRules/AutoCreationRules";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { formatCustomDateTime } from "../../../utils/dateUtils";

interface ChatTemplateCategory {
  chat_template_category_id: number;
  category_name: string;
  description: string;
  is_active: boolean;
  tenant_id: number;
  chat_template_type_id: number;
}

interface ChatTemplateMessage {
  chat_template_message_id: number;
  title: string;
  message: string;
  short_code: string;
  is_active: boolean;
  chat_template_category_id: number;
  chat_template_type_id: number;
  tenant_id: number;
}

interface ChatTemplatePlaceholder {
  chat_template_placeholder_id: number;
  chat_template_placeholder_key: string;
  chat_template_placeholder_display_name: string;
  description: string;
  is_active: boolean;
}

interface EmailProvider {
  mail_provider_id: number;
  mail_provider_display_name: string;
  mail_provider_key: string;
  mail_provider_logo: string | null;
  connected: boolean;
  mail_auth_id: number | null;
}

interface SocialProvider {
  social_auth_provider_id: number;
  social_auth_provider_key: string;
  social_auth_provider_display_name: string;
  connected: boolean;
  email: string | null;
  social_auth_id: number | null;
}

interface ChatBot {
  external_chat_tenant_bot_id: number;
  bot_name: string;
  bot_api_key: string | null;
  bot_customer_id?: string;
  bot_flow_key?: string;
  bot_key: string;
  bot_message_webhook_url: string;
  is_deleted: boolean;
  is_active: boolean;
  created_datetime: string;
  updated_datetime: string;
  bot_id: string;
  tenant_id: number;
  external_chat_platform_id: number;
  external_chat_platform?: {
    external_chat_platform_id: number;
    external_chat_platform_key: string;
    external_chat_platform_display_name: string;
  };
  chat_type?: {
    chat_type_id: number;
    chat_type_key: string;
    chat_type_display_name: string;
  };
  created_by: number;
  updated_by: number;
  seller_id?: string;
  seller_email?: string;
  is_authorized?: boolean;
}

interface WeekDay {
  week_day_id: number;
  week_day_key: string;
  week_day_display_name: string;
}

interface BusinessHour {
  tenant_business_hour_id?: number;
  start_time: string;
  end_time: string;
  time_zone: string;
  tenant_id: number;
  week_day_id: number;
  created_by?: number;
  updated_by?: number;
}

interface AutoReplySetting {
  tenant_chat_auto_reply_setting_id?: number;
  auto_reply_message: string;
  is_global: boolean;
  is_active: boolean;
  tenant_id: number;
  external_chat_bot_channel_account_id: number | null;
  external_chat_bot_channel_account?: {
    external_chat_bot_channel_account_id: number;
    account_name: string;
    external_chat_channel_key: string;
  };
  created_by?: number;
  updated_by?: number;
}

interface BotChannelAccount {
  external_chat_bot_channel_account_id: number;
  account_name: string;
  account_logo: string | null;
  is_active: boolean;
  is_visible_to_tenant: boolean;
  tenant_id: number;
  external_chat_channel_id: number;
  external_chat_tenant_bot_id: number;
  external_chat_tenant_bot: {
    external_chat_tenant_bot_id: number;
    bot_name: string;
  };
  external_chat_channel?: {
    external_chat_channel_id: number;
    external_chat_channel_key: string;
  } | null;
  shop_code?: string;
  shop_id?: string;
}

interface ExternalChatChannel {
  external_chat_channel_id: number;
  external_chat_channel_key: string;
  external_chat_channel_display_name: string;
  external_chat_platform?: {
    external_chat_platform_id: number;
    external_chat_platform_key: string;
    external_chat_platform_display_name: string;
  };
}

const route = all_routes;
const IntegrationSettings = () => {
  const { selectedTenantId, auth } = useContext(AuthContext);

  const MySwal = withReactContent(Swal);
  const userRole = useTenantRole(selectedTenantId || 0);

  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<
    "email" | "social" | "chatbot" | "privacy" | "allocation" | "calendar"
  >("email");
  const [emailSettingsTab, setEmailSettingsTab] = useState<"email-settings" | "auto-lead-creation">("email-settings");

  useEffect(() => {
    if (location.pathname.includes("chat-settings")) {
      setActiveTab("chatbot");
    } else if (location.pathname.includes("calendar-settings")) {
      setActiveTab("calendar");
    } else if (location.pathname.includes("auto-creation-rules")) {
      setActiveTab("email");
      setEmailSettingsTab("auto-lead-creation");
    } else {
      setActiveTab("email");
      setEmailSettingsTab("email-settings");
    }
  }, [location.pathname]);
  const [emailProviders, setEmailProviders] = useState<EmailProvider[]>([]);
  const [socialProviders, setSocialProviders] = useState<SocialProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [socialLoading, setSocialLoading] = useState(false);
  const [chatbotLoading, setChatbotLoading] = useState(false);
  const [chatbots, setChatbots] = useState<ChatBot[]>([]);
  const [showChatbotModal, setShowChatbotModal] = useState(false);
  const [showChannelAccountModal, setShowChannelAccountModal] = useState(false);
  const [showIntegrationSelectionModal, setShowIntegrationSelectionModal] = useState(false);
  const [isInternalChat, setIsInternalChat] = useState<boolean>(false);
  const [editingChatbot, setEditingChatbot] = useState<ChatBot | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [activeBrandIntegration, setActiveBrandIntegration] = useState<"TikTok" | "Lazada" | "Shopee" | null>(null);
  const [brandAuthLoading, setBrandAuthLoading] = useState(false);
  const [showBackfillModal, setShowBackfillModal] = useState(false);
  const [backfillIntegrationType, setBackfillIntegrationType] = useState("");
  const [backfillPeriod, setBackfillPeriod] = useState("1 Day");
  const [backfillStartDate, setBackfillStartDate] = useState("");
  const [backfillEndDate, setBackfillEndDate] = useState("");
  const [chatbotFormData, setChatbotFormData] = useState({
    bot_name: "",
    bot_id: "",
    bot_key: "",
    bot_message_webhook_url: "",
    bot_customer_id: "",
    bot_flow_key: "",
    bot_api_key: "",
  });

  const [selectedBotDetail, setSelectedBotDetail] = useState<ChatBot | null>(null);
  const [botChannelAccounts, setBotChannelAccounts] = useState<BotChannelAccount[]>([]);
  const [editingChannelAccount, setEditingChannelAccount] = useState<BotChannelAccount | null>(null);
  const [channelAccountLoading, setChannelAccountLoading] = useState(false);
  const [channelAccountFormData, setChannelAccountFormData] = useState({
    account_name: "",
    external_chat_channel_id: "",
    is_visible_to_tenant: false,
  });

  const [chatSettingsTab, setChatSettingsTab] = useState<"chatbot" | "status" | "privacy" | "team" | "template" | "autoreply">("chatbot");
  const [chatStatuses, setChatStatuses] = useState<{ id: string; name: string; order: number }[]>([]);
  const [chatStatusLoading, setChatStatusLoading] = useState(true);
  const initialChatStatusesRef = useRef<{ id: string; name: string; order: number }[]>([]);

  const [templateCategories, setTemplateCategories] = useState<ChatTemplateCategory[]>([]);
  const [templateMessages, setTemplateMessages] = useState<ChatTemplateMessage[]>([]);
  const [templatePlaceholders, setTemplatePlaceholders] = useState<ChatTemplatePlaceholder[]>([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ChatTemplateCategory | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ category_name: "", description: "" });
  const [messageFormData, setMessageFormData] = useState({ title: "", short_code: "", message: "" });
  const [editingMessage, setEditingMessage] = useState<ChatTemplateMessage | null>(null);
  const [editingCategory, setEditingCategory] = useState<ChatTemplateCategory | null>(null);
  const [openCategoryMenuId, setOpenCategoryMenuId] = useState<number | null>(null);
  const [showPlaceholderDropdown, setShowPlaceholderDropdown] = useState(false);
  const [placeholderSearch, setPlaceholderSearch] = useState("");
  const [filteredPlaceholders, setFilteredPlaceholders] = useState<ChatTemplatePlaceholder[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [initialBusinessHours, setInitialBusinessHours] = useState<BusinessHour[]>([]);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [autoReplySettings, setAutoReplySettings] = useState<AutoReplySetting[]>([]);
  const [initialAutoReplySettings, setInitialAutoReplySettings] = useState<AutoReplySetting[]>([]);
  const [channels, setChannels] = useState<BotChannelAccount[]>([]);
  const [externalChatChannels, setExternalChatChannels] = useState<ExternalChatChannel[]>([]);
  const [autoReplyLoading, setAutoReplyLoading] = useState(false);
  const [autoReplyTemplates, setAutoReplyTemplates] = useState<{ chat_template_message_id: number; title: string; message: string; short_code: string }[]>([]);
  const [activeTemplateDropdownIndex, setActiveTemplateDropdownIndex] = useState<number | null>(null);
  const [filteredAutoReplyTemplates, setFilteredAutoReplyTemplates] = useState<{ chat_template_message_id: number; title: string; message: string; short_code: string }[]>([]);
  const [expandedAutoReplyChannels, setExpandedAutoReplyChannels] = useState<Set<string>>(new Set());
  const [chatTypes, setChatTypes] = useState<any[]>([]);
  const [showNewAutoReplyForm, setShowNewAutoReplyForm] = useState(false);
  const [newAutoReplySetting, setNewAutoReplySetting] = useState<AutoReplySetting>({
    auto_reply_message: "",
    is_global: true,
    is_active: true,
    tenant_id: 0,
    external_chat_bot_channel_account_id: null,
  });

  const channelOptions = channels.map(chan => ({
    value: chan.external_chat_bot_channel_account_id,
    label: chan.account_name,
    key: chan.external_chat_channel?.external_chat_channel_key || ""
  }));

  const externalChatChannelOptions = externalChatChannels.map(chan => ({
    value: chan.external_chat_channel_id,
    label: chan.external_chat_channel_display_name,
    key: chan.external_chat_channel_key
  }));

  const availableChannelOptions = externalChatChannelOptions.filter(
    opt => !botChannelAccounts.some(
      account => account.external_chat_channel?.external_chat_channel_id === opt.value
    )
  );

  const formatChannelOption = (option: any) => (
    <div className="d-flex align-items-center">
      <i
        className={getChannelIcon(option.key)}
        style={{ color: getChannelColor(option.key), marginRight: '10px' }}
      ></i>
      <span>{option.label}</span>
    </div>
  );

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      height: '38px',
      minHeight: '38px',
      borderRadius: '6px',
      borderColor: '#e9ecef',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#0ea5e9'
      }
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f0f9ff' : 'white',
      color: '#333',
      cursor: 'pointer',
      fontSize: '14px',
      '&:active': {
        backgroundColor: '#e0f2fe'
      }
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: '14px'
    }),
    placeholder: (base: any) => ({
      ...base,
      fontSize: '14px'
    })
  };

  const fetchEmailProviders = async () => {
    const user_id = auth?.user_id;
    if (!user_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/lookup/mail-provider/?user_id=${user_id}&tenant_id=${selectedTenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEmailProviders(data);
      } else {
        console.error("Failed to fetch email providers");
      }
    } catch (error) {
      console.error("Error fetching email providers:", error);
    } finally {
      setLoading(false);
    }
  };



  const fetchChatbots = async () => {
    if (!selectedTenantId) {
      setChatbotLoading(false);
      return;
    }

    setChatbotLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/external-chat-tenant-bot/?tenant_id=${selectedTenantId}&is_deleted=false&is_active=true${auth?.user_id ? `&created_by=${auth.user_id}` : ""}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setChatbots(data.results || data || []);
      }
    } catch (error) {
      console.error("Error fetching chatbots:", error);
    } finally {
      setChatbotLoading(false);
    }
  };

  const fetchChatTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-type/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setChatTypes(data);
      }
    } catch (error) {
      console.error("Error fetching chat types:", error);
    }
  };

  const generateStatusKey = (displayName: string): string => {
    return displayName.trim().toUpperCase().replace(/\s+/g, '_');
  };

  const fetchChatStatuses = async () => {
    if (!selectedTenantId) {
      setChatStatusLoading(false);
      return;
    }

    setChatStatusLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/external-chat-end-user-status/?tenant_id=${selectedTenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const formatted = data.results
          .sort((a: any, b: any) => a.status_order - b.status_order)
          .map((s: any) => ({
            id: String(s.external_chat_end_user_status_id),
            name: s.external_chat_end_user_status_display_name,
            order: s.status_order,
          }));
        setChatStatuses(formatted);
        initialChatStatusesRef.current = formatted;
      } else {
        console.error("Failed to fetch chat statuses");
      }
    } catch (error) {
      console.error("Error fetching chat statuses:", error);
    } finally {
      setChatStatusLoading(false);
    }
  };

  const moveChatStatus = (sortedIdx: number, direction: number) => {
    const sorted = chatStatuses.slice().sort((a, b) => a.order - b.order);
    const targetIdx = sortedIdx + direction;
    if (sortedIdx === 0 || sortedIdx === sorted.length - 1) return;
    if (targetIdx <= 0 || targetIdx >= sorted.length - 1) return;
    [sorted[sortedIdx], sorted[targetIdx]] = [sorted[targetIdx], sorted[sortedIdx]];
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setChatStatuses(reOrdered);
  };

  const updateChatStatusName = (idx: number, value: string) => {
    const sorted = chatStatuses.slice().sort((a, b) => a.order - b.order);
    if (idx === 0 || idx === sorted.length - 1) return;
    sorted[idx].name = value;
    setChatStatuses(sorted.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const addChatStatus = () => {
    const sorted = chatStatuses.slice().sort((a, b) => a.order - b.order);
    const newStatus = {
      id: `s${Date.now()}`,
      name: "",
      order: 0,
    };
    if (sorted.length >= 1) {
      sorted.splice(sorted.length - 1, 0, newStatus);
    } else {
      sorted.push(newStatus);
    }
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setChatStatuses(reOrdered);
  };

  const removeChatStatus = (sortedIdx: number) => {
    const sorted = chatStatuses.slice().sort((a, b) => a.order - b.order);
    if (sortedIdx === 0 || sortedIdx === sorted.length - 1) return;
    sorted.splice(sortedIdx, 1);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setChatStatuses(reOrdered);
  };

  const saveChatStatuses = async () => {
    if (!selectedTenantId) {
      MySwal.fire({
        title: "Error",
        text: "Tenant ID not found.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const nonEmpty = chatStatuses.filter((s) => s.name.trim() !== "");
    const sorted = nonEmpty.slice().sort((a, b) => a.order - b.order);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));

    const originalIds = new Set(initialChatStatusesRef.current.map((s) => s.id));
    const currentIds = new Set(reOrdered.map((s) => s.id));

    const deletes = initialChatStatusesRef.current
      .filter((s) => !currentIds.has(s.id) && !s.id.startsWith("s"))
      .map((s) => Number(s.id));

    const inserts = reOrdered
      .filter((s) => s.id.startsWith("s") && s.name.trim() !== "")
      .map((s) => ({
        external_chat_end_user_status_key: generateStatusKey(s.name),
        external_chat_end_user_status_display_name: s.name.trim(),
        tenant_id: selectedTenantId,
        status_order: s.order,
      }));

    const updates = reOrdered
      .filter((s) => !s.id.startsWith("s"))
      .map((s) => ({
        external_chat_end_user_status_id: Number(s.id),
        external_chat_end_user_status_key: generateStatusKey(s.name),
        external_chat_end_user_status_display_name: s.name.trim(),
        status_order: s.order,
      }));

    const hasChanges = inserts.length > 0 || updates.length > 0 || deletes.length > 0;

    if (!hasChanges) {
      MySwal.fire({
        title: "No Changes",
        text: "No changes detected to save.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const payload = { updates, inserts, deletes };

      const response = await fetch(
        `${API_BASE_URL}/external-chat-end-user-status/batch/operations/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Status save error:", errorText);
        MySwal.fire({
          title: "Error!",
          text: "Error saving chat status(es).",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      MySwal.fire({
        title: "Success!",
        text: "Chat statuses saved successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      await fetchChatStatuses();
    } catch (error) {
      console.error("Save error:", error);
      MySwal.fire({
        title: "Error!",
        text: "An error occurred while saving.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const cancelChatStatusChanges = () => {
    setChatStatuses(initialChatStatusesRef.current);
  };

  const openAddChatbotModal = () => {
    setEditingChatbot(null);
    setChatbotFormData({
      bot_name: "",
      bot_id: "",
      bot_key: "",
      bot_message_webhook_url: "",
      bot_customer_id: "",
      bot_flow_key: "",
      bot_api_key: "",
    });
    setShowChatbotModal(true);
  };

  const openEditChatbotModal = (chatbot: ChatBot) => {
    setEditingChatbot(chatbot);
    setChatbotFormData({
      bot_name: chatbot.bot_name,
      bot_id: chatbot.bot_id,
      bot_key: chatbot.bot_key || "",
      bot_message_webhook_url: chatbot.bot_message_webhook_url,
      bot_customer_id: chatbot.bot_customer_id || "",
      bot_flow_key: chatbot.bot_flow_key || "",
      bot_api_key: chatbot.bot_api_key || "",
    });
    setShowChatbotModal(true);
    setActiveDropdown(null);
  };

  const closeChatbotModal = () => {
    setShowChatbotModal(false);
    setEditingChatbot(null);
    setChatbotFormData({
      bot_name: "",
      bot_id: "",
      bot_key: "",
      bot_message_webhook_url: "",
      bot_customer_id: "",
      bot_flow_key: "",
      bot_api_key: "",
    });
  };

  const handleSaveChatbot = async () => {
    if (!selectedTenantId || !auth?.user_id) {
      MySwal.fire({
        title: "Error",
        text: "Tenant ID or User ID not found.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!chatbotFormData.bot_name || !chatbotFormData.bot_id || !chatbotFormData.bot_message_webhook_url || !chatbotFormData.bot_customer_id || !chatbotFormData.bot_api_key) {
      MySwal.fire({
        title: "Error",
        text: "Please fill in all required fields.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const isDuplicate = chatbots.some(
      (bot) =>
        (bot.bot_id === chatbotFormData.bot_id || (chatbotFormData.bot_key && bot.bot_key === chatbotFormData.bot_key)) &&
        bot.external_chat_tenant_bot_id !== editingChatbot?.external_chat_tenant_bot_id
    );

    if (isDuplicate) {
      MySwal.fire({
        title: "Error",
        text: "You can't create another bot with the same Bot ID or Bot Key.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setChatbotLoading(true);

    try {
      const payload: any = {
        bot_name: chatbotFormData.bot_name,
        bot_id: chatbotFormData.bot_id,
        bot_key: chatbotFormData.bot_key,
        bot_message_webhook_url: chatbotFormData.bot_message_webhook_url,
        bot_customer_id: chatbotFormData.bot_customer_id,
        bot_flow_key: chatbotFormData.bot_flow_key,
        bot_api_key: chatbotFormData.bot_api_key,
        tenant_id: selectedTenantId,
        created_by: auth.user_id,
        updated_by: auth.user_id,
        external_chat_platform_id: 1,
      };

      if (!editingChatbot) {
        const externalChatType = chatTypes.find((ct: any) => ct.chat_type_key === "EXTERNAL");
        if (externalChatType) {
          payload.chat_type_id = externalChatType.chat_type_id;
        }
      }

      let url = `${API_BASE_URL}/external-chat-tenant-bot/`;
      let method = "POST";

      if (editingChatbot) {
        url = `${API_BASE_URL}/external-chat-tenant-bot/${editingChatbot.external_chat_tenant_bot_id}/`;
        method = "PATCH";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        MySwal.fire({
          title: "Success!",
          text: editingChatbot ? "Chatbot updated successfully!" : "Chatbot added successfully!",
          icon: "success",
          confirmButtonText: "OK",
        });
        closeChatbotModal();
        await fetchChatbots();
        if (selectedTenantId) {
          updateChatIntegrationStatus(selectedTenantId);
        }
      } else {
        MySwal.fire({
          title: "Error!",
          text: editingChatbot ? "Failed to update chatbot." : "Failed to add chatbot.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: "An error occurred while saving chatbot.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setChatbotLoading(false);
    }
  };

  const handleDeleteChatbot = async (chatbot: ChatBot) => {
    setActiveDropdown(null);

    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the chatbot "${chatbot.bot_name || "this chatbot"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setChatbotLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/external-chat-tenant-bot/${chatbot.external_chat_tenant_bot_id}/`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
          }
        );

        if (response.ok) {
          MySwal.fire({
            title: "Deleted!",
            text: "Chatbot deleted successfully!",
            icon: "success",
            confirmButtonText: "OK",
          });
          await fetchChatbots();
          if (selectedTenantId) {
            updateChatIntegrationStatus(selectedTenantId);
          }
        } else {
          MySwal.fire({
            title: "Error!",
            text: "Failed to delete chatbot.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        MySwal.fire({
          title: "Error!",
          text: "An error occurred while deleting chatbot.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setChatbotLoading(false);
      }
    }
  };

  const fetchBotChannelAccount = async (botId: number) => {
    if (!selectedTenantId) return;
    setChannelAccountLoading(true);
    try {
      const response = await ChatService.getBotChannelAccount(botId, Number(selectedTenantId));
      if (response && response.results && response.results.length > 0) {
        setBotChannelAccounts(response.results);
      } else {
        setBotChannelAccounts([]);
      }
      setEditingChannelAccount(null);
      setChannelAccountFormData({
        account_name: "",
        external_chat_channel_id: "",
        is_visible_to_tenant: false,
      });
    } catch (error) {
      console.error("Error fetching bot channel account:", error);
    } finally {
      setChannelAccountLoading(false);
    }
  };

  const handleBotClick = (chatbot: ChatBot) => {
    setSelectedBotDetail(chatbot);
    fetchBotChannelAccount(chatbot.external_chat_tenant_bot_id);
    if (channels.length === 0) {
      fetchChannels();
    }
    if (externalChatChannels.length === 0) {
      fetchExternalChatChannels();
    }
  };

  const handleSaveBotSettings = async () => {
    if (!selectedBotDetail || !selectedTenantId || !auth?.user_id) return;

    setChatbotLoading(true);
    try {
      const isExternalPlatform = selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE";
      const isInternalGoogleChat = selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL");
      const payload: any = {
        bot_name: selectedBotDetail.bot_name,
        tenant_id: selectedTenantId,
        updated_by: auth.user_id,
      };

      if (!isExternalPlatform && !isInternalGoogleChat) {
        payload.bot_id = selectedBotDetail.bot_id;
        payload.bot_key = selectedBotDetail.bot_key;
        payload.bot_message_webhook_url = selectedBotDetail.bot_message_webhook_url;
        payload.bot_customer_id = selectedBotDetail.bot_customer_id;
        payload.bot_flow_key = selectedBotDetail.bot_flow_key;
        payload.bot_api_key = selectedBotDetail.bot_api_key;
      }

      const response = await fetch(`${API_BASE_URL}/external-chat-tenant-bot/${selectedBotDetail.external_chat_tenant_bot_id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        MySwal.fire({ title: "Success!", text: "Bot settings updated successfully!", icon: "success", confirmButtonText: "OK" });
        await fetchChatbots();
        if (selectedTenantId) {
          updateChatIntegrationStatus(selectedTenantId);
        }
      } else {
        MySwal.fire({ title: "Error!", text: "Failed to update bot settings.", icon: "error", confirmButtonText: "OK" });
      }
    } catch (error) {
      console.error("Error saving bot settings:", error);
    } finally {
      setChatbotLoading(false);
    }
  };

  const handleSaveChannelAccount = async () => {
    if (!selectedBotDetail || !selectedTenantId || !auth?.user_id) return;

    const isExternalPlatform = selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE";
    const isInternalGoogleChat = selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL");

    if (!channelAccountFormData.account_name || (!isExternalPlatform && !isInternalGoogleChat && !channelAccountFormData.external_chat_channel_id)) {
      MySwal.fire({ title: "Error", text: "Please fill in all required fields.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    setChannelAccountLoading(true);
    try {
      const selectedChannel = externalChatChannels.find(ch => ch.external_chat_channel_id === Number(channelAccountFormData.external_chat_channel_id));
      const payload: any = {
        account_name: channelAccountFormData.account_name,
        tenant_id: selectedTenantId,
        external_chat_tenant_bot_id: selectedBotDetail.external_chat_tenant_bot_id,
        is_active: true,
        is_visible_to_tenant: channelAccountFormData.is_visible_to_tenant,
        created_by: auth.user_id,
        updated_by: auth.user_id,
      };

      if (!isExternalPlatform && !isInternalGoogleChat) {
        payload.external_chat_channel_id = Number(channelAccountFormData.external_chat_channel_id);
        payload.external_chat_platform_id = selectedChannel?.external_chat_platform?.external_chat_platform_id || null;
        if (!editingChannelAccount) {
          const externalChatType = chatTypes.find((ct: any) => ct.chat_type_key === "EXTERNAL");
          if (externalChatType) {
            payload.chat_type_id = externalChatType.chat_type_id;
          }
        }
      }

      let response;
      if (editingChannelAccount) {
        response = await ChatService.updateBotChannelAccount(editingChannelAccount.external_chat_bot_channel_account_id, payload);
      } else {
        response = await ChatService.createBotChannelAccount(payload);
      }

      if (response) {
        MySwal.fire({ title: "Success!", text: "Channel account saved successfully!", icon: "success", confirmButtonText: "OK" });
        fetchBotChannelAccount(selectedBotDetail.external_chat_tenant_bot_id);
        setShowChannelAccountModal(false);
      }
    } catch (error) {
      MySwal.fire({ title: "Error!", text: "Failed to save channel account.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setChannelAccountLoading(false);
    }
  };

  const handleEditChannelAccount = (account: BotChannelAccount) => {
    setEditingChannelAccount(account);
    setChannelAccountFormData({
      account_name: account.account_name,
      external_chat_channel_id: account.external_chat_channel?.external_chat_channel_id?.toString() || "",
      is_visible_to_tenant: account.is_visible_to_tenant,
    });
    setShowChannelAccountModal(true);
  };

  const handleCancelEditChannelAccount = () => {
    setEditingChannelAccount(null);
    setChannelAccountFormData({
      account_name: "",
      external_chat_channel_id: "",
      is_visible_to_tenant: false,
    });
  };

  const handleDeleteChannelAccount = async (account: BotChannelAccount) => {
    const result = await MySwal.fire({
      title: "Delete Channel Account?",
      text: `Are you sure you want to delete "${account.account_name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setChannelAccountLoading(true);
      try {
        await ChatService.deleteBotChannelAccount(account.external_chat_bot_channel_account_id);
        MySwal.fire({ title: "Deleted!", text: "Channel account has been deleted.", icon: "success", confirmButtonText: "OK" });
        if (selectedBotDetail) {
          fetchBotChannelAccount(selectedBotDetail.external_chat_tenant_bot_id);
        }
        if (editingChannelAccount?.external_chat_bot_channel_account_id === account.external_chat_bot_channel_account_id) {
          handleCancelEditChannelAccount();
        }
      } catch (error) {
        MySwal.fire({ title: "Error!", text: "Failed to delete channel account.", icon: "error", confirmButtonText: "OK" });
      } finally {
        setChannelAccountLoading(false);
      }
    }
  };

  const fetchTemplateCategories = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await ChatService.getChatTemplateCategories(Number(selectedTenantId));
      setTemplateCategories(response.results || []);
    } catch (error) { }
  };

  const fetchTemplateMessages = async (categoryId?: number) => {
    if (!selectedTenantId) return;
    try {
      const response = await ChatService.getChatTemplateMessages(Number(selectedTenantId), categoryId);
      setTemplateMessages(response.results || []);
    } catch (error) { }
  };

  const fetchTemplatePlaceholders = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await ChatService.getChatTemplatePlaceholders(Number(selectedTenantId));
      setTemplatePlaceholders(response.results || []);
    } catch (error) { }
  };

  const handleSaveCategory = async () => {
    if (!selectedTenantId || !auth?.user_id || !categoryFormData.category_name.trim()) {
      MySwal.fire({ title: "Error", text: "Please fill in category name.", icon: "error", confirmButtonText: "OK" });
      return;
    }
    setTemplateLoading(true);
    try {
      if (editingCategory) {
        await ChatService.updateChatTemplateCategory(editingCategory.chat_template_category_id, {
          category_name: categoryFormData.category_name.trim(),
          description: categoryFormData.description.trim(),
          tenant_id: Number(selectedTenantId),
          updated_by: auth.user_id,
        });
        MySwal.fire({ title: "Success!", text: "Category updated successfully!", icon: "success", confirmButtonText: "OK" });
      } else {
        await ChatService.createChatTemplateCategory({
          category_name: categoryFormData.category_name.trim(),
          description: categoryFormData.description.trim(),
          tenant_id: Number(selectedTenantId),
          chat_template_type_id: 1,
          created_by: auth.user_id,
          updated_by: auth.user_id,
        });
        MySwal.fire({ title: "Success!", text: "Category created successfully!", icon: "success", confirmButtonText: "OK" });
      }
      setShowCategoryModal(false);
      setCategoryFormData({ category_name: "", description: "" });
      setEditingCategory(null);
      await fetchTemplateCategories();
    } catch (error) {
      MySwal.fire({ title: "Error!", text: editingCategory ? "Failed to update category." : "Failed to create category.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleDeleteCategory = async (category: ChatTemplateCategory) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Delete category "${category.category_name}"? All messages in this category will also be deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete it!",
    });
    if (result.isConfirmed) {
      setTemplateLoading(true);
      try {
        await ChatService.deleteChatTemplateCategory(category.chat_template_category_id);
        MySwal.fire({ title: "Deleted!", text: "Category deleted successfully!", icon: "success", confirmButtonText: "OK" });
        if (selectedCategory?.chat_template_category_id === category.chat_template_category_id) {
          setSelectedCategory(null);
          setTemplateMessages([]);
        }
        await fetchTemplateCategories();
      } catch (error) {
        MySwal.fire({ title: "Error!", text: "Failed to delete category.", icon: "error", confirmButtonText: "OK" });
      } finally {
        setTemplateLoading(false);
      }
    }
  };

  const handleSaveMessage = async () => {
    if (!selectedTenantId || !auth?.user_id || !selectedCategory) {
      MySwal.fire({ title: "Error", text: "Please select a category first.", icon: "error", confirmButtonText: "OK" });
      return;
    }
    if (!messageFormData.title.trim() || !messageFormData.short_code.trim() || !messageFormData.message.trim()) {
      MySwal.fire({ title: "Error", text: "Please fill in all fields.", icon: "error", confirmButtonText: "OK" });
      return;
    }
    let shortCode = messageFormData.short_code.trim();
    if (!shortCode.startsWith("/")) shortCode = "/" + shortCode;
    setTemplateLoading(true);
    try {
      if (editingMessage) {
        await ChatService.updateChatTemplateMessage(editingMessage.chat_template_message_id, {
          title: messageFormData.title.trim(),
          message: messageFormData.message.trim(),
          short_code: shortCode,
          tenant_id: Number(selectedTenantId),
          updated_by: auth.user_id,
        });
        MySwal.fire({ title: "Success!", text: "Template message updated successfully!", icon: "success", confirmButtonText: "OK" });
      } else {
        await ChatService.createChatTemplateMessage({
          title: messageFormData.title.trim(),
          message: messageFormData.message.trim(),
          short_code: shortCode,
          chat_template_category_id: selectedCategory.chat_template_category_id,
          chat_template_type_id: 1,
          tenant_id: Number(selectedTenantId),
          created_by: auth.user_id,
          updated_by: auth.user_id,
        });
        MySwal.fire({ title: "Success!", text: "Template message created successfully!", icon: "success", confirmButtonText: "OK" });
      }
      setShowMessageModal(false);
      setMessageFormData({ title: "", short_code: "", message: "" });
      setEditingMessage(null);
      await fetchTemplateMessages(selectedCategory.chat_template_category_id);
    } catch (error) {
      MySwal.fire({ title: "Error!", text: editingMessage ? "Failed to update template message." : "Failed to create template message.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setTemplateLoading(false);
    }
  };

  const openEditMessageModal = (message: ChatTemplateMessage) => {
    setEditingMessage(message);
    setMessageFormData({
      title: message.title,
      short_code: message.short_code,
      message: message.message,
    });
    setShowMessageModal(true);
  };

  const handleDeleteMessage = async (message: ChatTemplateMessage) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Delete template "${message.title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete it!",
    });
    if (result.isConfirmed) {
      setTemplateLoading(true);
      try {
        await ChatService.deleteChatTemplateMessage(message.chat_template_message_id);
        MySwal.fire({ title: "Deleted!", text: "Template deleted successfully!", icon: "success", confirmButtonText: "OK" });
        if (selectedCategory) {
          await fetchTemplateMessages(selectedCategory.chat_template_category_id);
        }
      } catch (error) {
        MySwal.fire({ title: "Error!", text: "Failed to delete template.", icon: "error", confirmButtonText: "OK" });
      } finally {
        setTemplateLoading(false);
      }
    }
  };

  const insertPlaceholder = (placeholderKey: string) => {
    const message = messageFormData.message;
    const textBeforeCursor = message.substring(0, cursorPosition);
    const lastOpenBrace = textBeforeCursor.lastIndexOf("{{");
    if (lastOpenBrace !== -1) {
      const beforeBrace = message.substring(0, lastOpenBrace);
      const afterCursor = message.substring(cursorPosition);
      const newMessage = beforeBrace + `{{${placeholderKey}}}` + afterCursor;
      setMessageFormData((prev) => ({
        ...prev,
        message: newMessage,
      }));
      const newCursorPos = beforeBrace.length + `{{${placeholderKey}}}`.length;
      setCursorPosition(newCursorPos);
      setTimeout(() => {
        if (messageTextareaRef.current) {
          messageTextareaRef.current.focus();
          messageTextareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    } else {
      setMessageFormData((prev) => ({
        ...prev,
        message: prev.message + `{{${placeholderKey}}}`,
      }));
    }
    setShowPlaceholderDropdown(false);
    setPlaceholderSearch("");
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setCursorPosition(cursorPos);
    setMessageFormData({ ...messageFormData, message: value });

    const textBeforeCursor = value.substring(0, cursorPos);
    const lastOpenBraceIndex = textBeforeCursor.lastIndexOf("{{");
    if (lastOpenBraceIndex !== -1) {
      const textAfterBrace = textBeforeCursor.substring(lastOpenBraceIndex + 2);
      if (!textAfterBrace.includes("}}")) {
        setPlaceholderSearch(textAfterBrace.toLowerCase());
        const filtered = templatePlaceholders.filter(
          (ph) =>
            ph.chat_template_placeholder_key.toLowerCase().includes(textAfterBrace.toLowerCase()) ||
            ph.chat_template_placeholder_display_name.toLowerCase().includes(textAfterBrace.toLowerCase())
        );
        setFilteredPlaceholders(filtered);
        setShowPlaceholderDropdown(true);
      } else {
        setShowPlaceholderDropdown(false);
      }
    } else {
      setShowPlaceholderDropdown(false);
    }
  };

  const fetchWeekDays = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/week-day/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setWeekDays(data);
      }
    } catch (error) { }
  };

  const fetchBusinessHours = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/tenant-business-hour/?tenant_id=${selectedTenantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const hours = data.results || [];
        setBusinessHours(hours);
        setInitialBusinessHours(hours);
        if (hours.length > 0 && hours[0].time_zone) {
          setSelectedTimezone(hours[0].time_zone);
        }
      }
    } catch (error) { }
  };

  const fetchAutoReplySettings = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/tenant-chat-auto-reply-setting/?tenant_id=${selectedTenantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const settings = data.results || [];
        setAutoReplySettings(settings);
        setInitialAutoReplySettings(settings);
      }
    } catch (error) { }
  };

  const fetchChannels = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/external-chat-bot-channel-account/?tenant_id=${selectedTenantId}&is_active=true`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data.results || []);
      }
    } catch (error) { }
  };

  const fetchExternalChatChannels = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/external-chat-channel/?tenant_id=${selectedTenantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setExternalChatChannels(data.results || []);
      }
    } catch (error) { }
  };

  const fetchAutoReplyTemplates = async () => {
    if (!selectedTenantId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/chat-template-message/?tenant_id=${selectedTenantId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAutoReplyTemplates(data.results || []);
      }
    } catch (error) { }
  };

  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const toggleBusinessHourDay = (weekDayId: number) => {
    const existing = businessHours.find((bh) => bh.week_day_id === weekDayId);
    if (existing) {
      setBusinessHours(businessHours.filter((bh) => bh.week_day_id !== weekDayId));
    } else {
      const newBusinessHour: BusinessHour = {
        start_time: "09:00:00",
        end_time: "17:00:00",
        time_zone: selectedTimezone,
        tenant_id: Number(selectedTenantId),
        week_day_id: weekDayId,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      };
      setBusinessHours([...businessHours, newBusinessHour]);
    }
  };

  const updateBusinessHourTime = (weekDayId: number, field: "start_time" | "end_time", value: string) => {
    setBusinessHours(
      businessHours.map((bh) =>
        bh.week_day_id === weekDayId ? { ...bh, [field]: value + ":00", updated_by: auth?.user_id } : bh
      )
    );
  };

  const handleTimezoneChange = (newTimezone: string) => {
    setSelectedTimezone(newTimezone);
    setBusinessHours(
      businessHours.map((bh) => ({ ...bh, time_zone: newTimezone, updated_by: auth?.user_id }))
    );
  };

  const hasBusinessHoursChanged = () => {
    if (businessHours.length !== initialBusinessHours.length) return true;
    for (const bh of businessHours) {
      const initialBh = initialBusinessHours.find((ib) => ib.week_day_id === bh.week_day_id);
      if (!initialBh) return true;
      if (bh.start_time !== initialBh.start_time) return true;
      if (bh.end_time !== initialBh.end_time) return true;
      if (bh.time_zone !== initialBh.time_zone) return true;
    }
    return false;
  };

  const saveBusinessHours = async () => {
    if (!selectedTenantId || !auth?.user_id) {
      MySwal.fire({ title: "Error", text: "Tenant ID or User ID not found.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    setAutoReplyLoading(true);

    const initialIds = new Set(initialBusinessHours.map((bh) => bh.tenant_business_hour_id));
    const currentIds = new Set(businessHours.filter((bh) => bh.tenant_business_hour_id).map((bh) => bh.tenant_business_hour_id));

    const deletes = initialBusinessHours
      .filter((bh) => bh.tenant_business_hour_id && !businessHours.some((cbh) => cbh.tenant_business_hour_id === bh.tenant_business_hour_id))
      .map((bh) => bh.tenant_business_hour_id as number);

    const inserts = businessHours
      .filter((bh) => !bh.tenant_business_hour_id)
      .map((bh) => ({
        start_time: bh.start_time,
        end_time: bh.end_time,
        time_zone: bh.time_zone,
        tenant_id: Number(selectedTenantId),
        week_day_id: bh.week_day_id,
        created_by: auth.user_id,
        updated_by: auth.user_id,
      }));

    const updates = businessHours
      .filter((bh) => bh.tenant_business_hour_id)
      .map((bh) => ({
        tenant_business_hour_id: bh.tenant_business_hour_id,
        start_time: bh.start_time,
        end_time: bh.end_time,
        time_zone: bh.time_zone,
        tenant_id: Number(selectedTenantId),
        week_day_id: bh.week_day_id,
        updated_by: auth.user_id,
      }));

    try {
      const response = await fetch(`${API_BASE_URL}/tenant-business-hour/batch/operations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify({ updates, inserts, deletes }),
      });

      if (response.ok) {
        MySwal.fire({ title: "Success!", text: "Business hours saved successfully!", icon: "success", confirmButtonText: "OK" });
        await fetchBusinessHours();
      } else {
        MySwal.fire({ title: "Error!", text: "Failed to save business hours.", icon: "error", confirmButtonText: "OK" });
      }
    } catch (error) {
      MySwal.fire({ title: "Error!", text: "An error occurred while saving business hours.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setAutoReplyLoading(false);
    }
  };

  const addAutoReplySetting = () => {
    setNewAutoReplySetting({
      auto_reply_message: "",
      is_global: true,
      is_active: true,
      tenant_id: Number(selectedTenantId),
      external_chat_bot_channel_account_id: null,
      created_by: auth?.user_id,
      updated_by: auth?.user_id,
    });
    setShowNewAutoReplyForm(true);
  };

  const confirmNewAutoReplySetting = async () => {
    if (newAutoReplySetting.auto_reply_message.trim() === "") {
      MySwal.fire({ title: "Error", text: "Please enter an auto-reply message.", icon: "error", confirmButtonText: "OK" });
      return;
    }
    if (!newAutoReplySetting.is_global && !newAutoReplySetting.external_chat_bot_channel_account_id) {
      MySwal.fire({ title: "Error", text: "Please select a channel or mark as global.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    setAutoReplyLoading(true);

    try {
      const insertData = {
        auto_reply_message: newAutoReplySetting.auto_reply_message,
        is_global: newAutoReplySetting.is_global,
        is_active: newAutoReplySetting.is_active,
        tenant_id: Number(selectedTenantId),
        external_chat_bot_channel_account_id: newAutoReplySetting.is_global ? null : newAutoReplySetting.external_chat_bot_channel_account_id,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      };

      const response = await fetch(`${API_BASE_URL}/tenant-chat-auto-reply-setting/batch/operations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify({ updates: [], inserts: [insertData], deletes: [] }),
      });

      if (response.ok) {
        MySwal.fire({ title: "Success!", text: "Auto-reply message added successfully!", icon: "success", confirmButtonText: "OK" });
        await fetchAutoReplySettings();
        setShowNewAutoReplyForm(false);
        setNewAutoReplySetting({
          auto_reply_message: "",
          is_global: true,
          is_active: true,
          tenant_id: Number(selectedTenantId),
          external_chat_bot_channel_account_id: null,
        });
      } else {
        MySwal.fire({ title: "Error!", text: "Failed to add auto-reply message.", icon: "error", confirmButtonText: "OK" });
      }
    } catch (error) {
      MySwal.fire({ title: "Error!", text: "An error occurred while adding auto-reply message.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setAutoReplyLoading(false);
    }
  };

  const cancelNewAutoReplySetting = () => {
    setShowNewAutoReplyForm(false);
    setNewAutoReplySetting({
      auto_reply_message: "",
      is_global: true,
      is_active: true,
      tenant_id: Number(selectedTenantId),
      external_chat_bot_channel_account_id: null,
    });
  };

  const updateAutoReplySetting = (index: number, field: keyof AutoReplySetting, value: any) => {
    setAutoReplySettings(
      autoReplySettings.map((setting, i) => {
        if (i === index) {
          const updated = { ...setting, [field]: value, updated_by: auth?.user_id };
          if (field === "is_global" && value === true) {
            updated.external_chat_bot_channel_account_id = null;
          }
          return updated;
        }
        return setting;
      })
    );
  };

  const removeAutoReplySetting = async (index: number) => {
    const setting = autoReplySettings[index];
    const result = await MySwal.fire({
      title: "Delete Auto-Reply?",
      text: "Are you sure you want to delete this auto-reply message? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      if (setting.tenant_chat_auto_reply_setting_id) {
        try {
          const response = await fetch(`${API_BASE_URL}/tenant-chat-auto-reply-setting/batch/operations/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
            body: JSON.stringify({ updates: [], inserts: [], deletes: [setting.tenant_chat_auto_reply_setting_id] }),
          });

          if (response.ok) {
            MySwal.fire({ title: "Deleted!", text: "Auto-reply message has been deleted.", icon: "success", confirmButtonText: "OK" });
            setAutoReplySettings(autoReplySettings.filter((_, i) => i !== index));
            setInitialAutoReplySettings(initialAutoReplySettings.filter((s) => s.tenant_chat_auto_reply_setting_id !== setting.tenant_chat_auto_reply_setting_id));
          } else {
            MySwal.fire({ title: "Error!", text: "Failed to delete auto-reply message.", icon: "error", confirmButtonText: "OK" });
          }
        } catch (error) {
          MySwal.fire({ title: "Error!", text: "An error occurred while deleting.", icon: "error", confirmButtonText: "OK" });
        }
      } else {
        setAutoReplySettings(autoReplySettings.filter((_, i) => i !== index));
      }
    }
  };

  const saveChannelAutoReplySettings = async (channelKey: string) => {
    if (!selectedTenantId || !auth?.user_id) {
      MySwal.fire({ title: "Error", text: "Tenant ID or User ID not found.", icon: "error", confirmButtonText: "OK" });
      return;
    }


    const initialChannelSettings = initialAutoReplySettings.filter((s) => {
      if (channelKey === "GLOBAL") {
        return s.is_global;
      } else {
        const channel = channels.find((c) => c.external_chat_channel?.external_chat_channel_key === channelKey);
        return !s.is_global && channel && s.external_chat_bot_channel_account_id === channel.external_chat_bot_channel_account_id;
      }
    });


    const initialSettingIds = new Set(initialChannelSettings.map((s) => s.tenant_chat_auto_reply_setting_id));

    const channelSettings = autoReplySettings.filter((s) => {
      if (s.tenant_chat_auto_reply_setting_id && initialSettingIds.has(s.tenant_chat_auto_reply_setting_id)) {
        return true;
      }

      if (!s.tenant_chat_auto_reply_setting_id) {
        if (channelKey === "GLOBAL") {
          return s.is_global;
        } else {
          const channel = channels.find((c) => c.external_chat_channel?.external_chat_channel_key === channelKey);
          return !s.is_global && channel && s.external_chat_bot_channel_account_id === channel.external_chat_bot_channel_account_id;
        }
      }
      return false;
    });

    const validSettings = channelSettings.filter((s) => s.auto_reply_message.trim() !== "");
    if (validSettings.length === 0 && channelSettings.length > 0) {
      MySwal.fire({ title: "Error", text: "Please enter an auto-reply message.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    setAutoReplyLoading(true);

    const deletes = initialChannelSettings
      .filter((s) => s.tenant_chat_auto_reply_setting_id && !channelSettings.some((cs) => cs.tenant_chat_auto_reply_setting_id === s.tenant_chat_auto_reply_setting_id))
      .map((s) => s.tenant_chat_auto_reply_setting_id as number);

    const inserts = validSettings
      .filter((s) => !s.tenant_chat_auto_reply_setting_id)
      .map((s) => ({
        auto_reply_message: s.auto_reply_message,
        is_global: s.is_global,
        is_active: s.is_active,
        tenant_id: Number(selectedTenantId),
        external_chat_bot_channel_account_id: s.is_global ? null : s.external_chat_bot_channel_account_id,
        created_by: auth.user_id,
        updated_by: auth.user_id,
      }));

    const updates = validSettings
      .filter((s) => s.tenant_chat_auto_reply_setting_id)
      .map((s) => ({
        tenant_chat_auto_reply_setting_id: s.tenant_chat_auto_reply_setting_id,
        auto_reply_message: s.auto_reply_message,
        is_global: s.is_global,
        is_active: s.is_active,
        tenant_id: Number(selectedTenantId),
        external_chat_bot_channel_account_id: s.is_global ? null : s.external_chat_bot_channel_account_id,
        updated_by: auth.user_id,
      }));

    try {
      const response = await fetch(`${API_BASE_URL}/tenant-chat-auto-reply-setting/batch/operations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify({ updates, inserts, deletes }),
      });

      if (response.ok) {
        const channelName = channelKey === "GLOBAL" ? "All Channels" : channels.find((c) => c.external_chat_channel?.external_chat_channel_key === channelKey)?.account_name || channelKey;
        MySwal.fire({ title: "Success!", text: `${channelName} auto-reply settings saved successfully!`, icon: "success", confirmButtonText: "OK" });
        await fetchAutoReplySettings();
      } else {
        MySwal.fire({ title: "Error!", text: "Failed to save auto-reply settings.", icon: "error", confirmButtonText: "OK" });
      }
    } catch (error) {
      MySwal.fire({ title: "Error!", text: "An error occurred while saving auto-reply settings.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setAutoReplyLoading(false);
    }
  };

  const cancelChannelAutoReplyChanges = (channelKey: string) => {
    const channelId = channelKey === "GLOBAL" ? null : channels.find((c) => c.external_chat_channel?.external_chat_channel_key === channelKey)?.external_chat_bot_channel_account_id;

    const otherSettings = autoReplySettings.filter((s) => {
      if (channelKey === "GLOBAL") {
        return !s.is_global;
      } else {
        return s.is_global || s.external_chat_bot_channel_account_id !== channelId;
      }
    });

    const restoredSettings = initialAutoReplySettings.filter((s) => {
      if (channelKey === "GLOBAL") {
        return s.is_global;
      } else {
        return !s.is_global && s.external_chat_bot_channel_account_id === channelId;
      }
    });

    setAutoReplySettings([...otherSettings, ...restoredSettings]);
  };

  const saveAutoReplySettings = async () => {
    if (!selectedTenantId || !auth?.user_id) {
      MySwal.fire({ title: "Error", text: "Tenant ID or User ID not found.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    const validSettings = autoReplySettings.filter((s) => s.auto_reply_message.trim() !== "");
    if (validSettings.length === 0 && autoReplySettings.length > 0) {
      MySwal.fire({ title: "Error", text: "Please enter an auto-reply message.", icon: "error", confirmButtonText: "OK" });
      return;
    }

    setAutoReplyLoading(true);

    const deletes = initialAutoReplySettings
      .filter((s) => s.tenant_chat_auto_reply_setting_id && !autoReplySettings.some((cs) => cs.tenant_chat_auto_reply_setting_id === s.tenant_chat_auto_reply_setting_id))
      .map((s) => s.tenant_chat_auto_reply_setting_id as number);

    const inserts = validSettings
      .filter((s) => !s.tenant_chat_auto_reply_setting_id)
      .map((s) => ({
        auto_reply_message: s.auto_reply_message,
        is_global: s.is_global,
        is_active: s.is_active,
        tenant_id: Number(selectedTenantId),
        external_chat_bot_channel_account_id: s.is_global ? null : s.external_chat_bot_channel_account_id,
        created_by: auth.user_id,
        updated_by: auth.user_id,
      }));

    const updates = validSettings
      .filter((s) => s.tenant_chat_auto_reply_setting_id)
      .map((s) => ({
        tenant_chat_auto_reply_setting_id: s.tenant_chat_auto_reply_setting_id,
        auto_reply_message: s.auto_reply_message,
        is_global: s.is_global,
        is_active: s.is_active,
        tenant_id: Number(selectedTenantId),
        external_chat_bot_channel_account_id: s.is_global ? null : s.external_chat_bot_channel_account_id,
        updated_by: auth.user_id,
      }));

    try {
      const response = await fetch(`${API_BASE_URL}/tenant-chat-auto-reply-setting/batch/operations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
        body: JSON.stringify({ updates, inserts, deletes }),
      });

      if (response.ok) {
        MySwal.fire({ title: "Success!", text: "Auto-reply settings saved successfully!", icon: "success", confirmButtonText: "OK" });
        await fetchAutoReplySettings();
      } else {
        MySwal.fire({ title: "Error!", text: "Failed to save auto-reply settings.", icon: "error", confirmButtonText: "OK" });
      }
    } catch (error) {
      MySwal.fire({ title: "Error!", text: "An error occurred while saving auto-reply settings.", icon: "error", confirmButtonText: "OK" });
    } finally {
      setAutoReplyLoading(false);
    }
  };

  const cancelAutoReplyChanges = () => {
    setBusinessHours(initialBusinessHours);
    setAutoReplySettings(initialAutoReplySettings);
  };

  const getChannelIcon = (channelKey: string) => {
    switch (channelKey) {
      case "SMS":
        return "fa-solid fa-message";
      case "WHATSAPP":
        return "fa-brands fa-whatsapp";
      case "FACEBOOK":
        return "fa-brands fa-facebook";
      case "INSTAGRAM":
        return "fa-brands fa-instagram";
      case "LINE":
        return "fa-brands fa-line";
      case "SLACK":
        return "fa-brands fa-slack";
      case "TELEGRAM":
        return "fa-brands fa-telegram";
      case "WEB":
        return "bi bi-display";
      default:
        return "fa-solid fa-globe";
    }
  };

  const getChannelColor = (channelKey: string) => {
    switch (channelKey) {
      case "SMS":
        return "#4caf50";
      case "WHATSAPP":
        return "#25D366";
      case "FACEBOOK":
        return "#1877F2";
      case "INSTAGRAM":
        return "#E4405F";
      case "LINE":
        return "#00B900";
      case "SLACK":
        return "#4A154B";
      case "TELEGRAM":
        return "#2AABEE";
      case "WEB":
        return "#AE1536";
      case "GLOBAL":
        return "#6366f1";
      default:
        return "#666666";
    }
  };

  const getChannelBgColor = (channelKey: string) => {
    switch (channelKey) {
      case "SMS":
        return "rgba(76, 175, 80, 0.1)";
      case "WHATSAPP":
        return "rgba(37, 211, 102, 0.1)";
      case "FACEBOOK":
        return "rgba(24, 119, 242, 0.1)";
      case "INSTAGRAM":
        return "rgba(228, 64, 95, 0.1)";
      case "LINE":
        return "rgba(0, 185, 0, 0.1)";
      case "SLACK":
        return "rgba(74, 21, 75, 0.1)";
      case "TELEGRAM":
        return "rgba(42, 171, 238, 0.1)";
      case "WEB":
        return "rgba(174, 21, 54, 0.1)";
      case "GLOBAL":
        return "rgba(99, 102, 241, 0.1)";
      default:
        return "rgba(102, 102, 102, 0.1)";
    }
  };

  const toggleAutoReplyChannel = (channelKey: string) => {
    setExpandedAutoReplyChannels((prev) => {
      if (prev.has(channelKey)) {
        return new Set();
      } else {
        return new Set([channelKey]);
      }
    });
  };

  interface GroupedAutoReply {
    channelKey: string;
    channelName: string;
    settings: { setting: AutoReplySetting; originalIndex: number }[];
  }

  const getGroupedAutoReplySettings = (): GroupedAutoReply[] => {
    const groups: Map<string, GroupedAutoReply> = new Map();

    groups.set("GLOBAL", {
      channelKey: "GLOBAL",
      channelName: "All Channels",
      settings: [],
    });

    channels.forEach((channel) => {
      groups.set((channel.external_chat_channel?.external_chat_channel_key || "") + "_" + channel.external_chat_bot_channel_account_id, {
        channelKey: channel.external_chat_channel?.external_chat_channel_key || "",
        channelName: channel.account_name,
        settings: [],
      });
    });

    autoReplySettings.forEach((setting, index) => {
      const initialSetting = initialAutoReplySettings[index] || setting;
      if (initialSetting.is_global) {
        const group = groups.get("GLOBAL");
        if (group) {
          group.settings.push({ setting, originalIndex: index });
        }
      } else if (initialSetting.external_chat_bot_channel_account_id) {
        const channel = channels.find((c) => c.external_chat_bot_channel_account_id === initialSetting.external_chat_bot_channel_account_id);
        if (channel) {
          const group = groups.get((channel.external_chat_channel?.external_chat_channel_key || "") + "_" + channel.external_chat_bot_channel_account_id);
          if (group) {
            group.settings.push({ setting, originalIndex: index });
          }
        } else if (initialSetting.external_chat_bot_channel_account) {
          const groupKey = initialSetting.external_chat_bot_channel_account.external_chat_channel_key + "_" + initialSetting.external_chat_bot_channel_account_id;
          if (!groups.has(groupKey)) {
            groups.set(groupKey, {
              channelKey: initialSetting.external_chat_bot_channel_account.external_chat_channel_key,
              channelName: initialSetting.external_chat_bot_channel_account.account_name,
              settings: [],
            });
          }
          const group = groups.get(groupKey);
          if (group) {
            group.settings.push({ setting, originalIndex: index });
          }
        }
      }
    });

    return Array.from(groups.values()).filter((g) => g.settings.length > 0);
  };

  useEffect(() => {
    fetchEmailProviders();
    fetchChatStatuses();
    fetchTemplateCategories();
    fetchTemplatePlaceholders();
    fetchWeekDays();
    fetchBusinessHours();
    fetchAutoReplySettings();
    fetchChannels();
    fetchAutoReplyTemplates();
    fetchChatTypes();
  }, [auth?.user_id, selectedTenantId]);

  useEffect(() => {
    fetchChatbots();
  }, [selectedTenantId, userRole?.role_key]);


  const getProviderStatus = (providerKey: string) => {
    return emailProviders.find(
      (provider) => provider.mail_provider_key === providerKey
    );
  };

  const getSocialProviderStatus = (providerKey: string) => {
    return socialProviders.find(
      (provider) => provider.social_auth_provider_key === providerKey
    );
  };

  const handleUnlinkAccount = async (
    providerKey: string,
    providerName: string
  ) => {
    const provider = getProviderStatus(providerKey);
    if (!provider || !provider.mail_auth_id) {
      MySwal.fire({
        title: "Error",
        text: "Provider not found or not connected.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to unlink the ${providerName} account?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Unlink it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/mail-auth/${provider.mail_auth_id}/`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
          }
        );

        if (response.ok) {
          MySwal.fire({
            title: "Success!",
            text: `${providerName} account unlinked successfully!`,
            icon: "success",
            confirmButtonText: "OK",
          });

          await fetchEmailProviders();
        } else {
          MySwal.fire({
            title: "Error!",
            text: `Failed to unlink ${providerName} account.`,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        MySwal.fire({
          title: "Error!",
          text: `An error occurred while unlinking ${providerName} account.`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const handleSocialUnlinkAccount = async (
    providerKey: string,
    providerName: string
  ) => {
    const provider = getSocialProviderStatus(providerKey);
    if (!provider || !provider.social_auth_id) {
      MySwal.fire({
        title: "Error",
        text: "Provider not found or not connected.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to unlink the ${providerName} account?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Unlink it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/social-auth/${provider.social_auth_id}/`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${localStorage.getItem("authCred")}`,
            },
          }
        );

        if (response.ok) {
          MySwal.fire({
            title: "Success!",
            text: `${providerName} account unlinked successfully!`,
            icon: "success",
            confirmButtonText: "OK",
          });
        } else {
          MySwal.fire({
            title: "Error!",
            text: `Failed to unlink ${providerName} account.`,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        MySwal.fire({
          title: "Error!",
          text: `An error occurred while unlinking ${providerName} account.`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const handleIntegrationConnect = async (integrationType: string) => {
    const user_id = auth?.user_id;
    if (!user_id) {
      MySwal.fire({
        title: "Error",
        text: "User ID not found. Please login again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const isEmailIntegration =
      integrationType === "Google" || integrationType === "microsoft";

    if (isEmailIntegration) {
      setBackfillIntegrationType(integrationType);
      setBackfillPeriod("1 Day");
      setBackfillStartDate("");
      setBackfillEndDate("");
      setShowBackfillModal(true);
      return;
    }

    const isSocialIntegration = integrationType === "LINE";
    const connectText = isEmailIntegration
      ? `${integrationType} Mail`
      : integrationType;

    MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to connect to ${connectText}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Connect it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let apiPath, integrationEndpoint, method;

          if (isEmailIntegration) {
            apiPath = "mail";
            integrationEndpoint = integrationType.toLowerCase();
            method = "POST";
          } else if (isSocialIntegration) {
            apiPath = "social";
            integrationEndpoint = integrationType.toLowerCase();
            method = "GET";
          } else {
            apiPath = "social";
            integrationEndpoint = integrationType.toLowerCase();
            method = "POST";
          }

          const response = await fetch(
            `${API_BASE_URL}/${apiPath}/${integrationEndpoint}/auth/?user_id=${user_id}`,
            {
              method,
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${localStorage.getItem("authCred")}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.auth_url) {
              window.location.href = data.auth_url;
            } else {
              MySwal.fire({
                title: "Success!",
                text: `${connectText} integration connected successfully!`,
                icon: "success",
                confirmButtonText: "OK",
              });
              if (isEmailIntegration) {
                await fetchEmailProviders();
              }
            }
          } else {
            MySwal.fire({
              title: "Error!",
              text: `Failed to connect ${connectText} integration.`,
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          MySwal.fire({
            title: "Error!",
            text: `An error occurred while connecting ${connectText} integration.`,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const proceedWithEmailIntegration = async () => {
    const user_id = auth?.user_id;
    if (!user_id) return;

    if (backfillPeriod === "Custom") {
      if (!backfillStartDate || !backfillEndDate) {
        MySwal.fire({ title: "Error", text: "Please select both start and end dates.", icon: "error", confirmButtonText: "OK" });
        return;
      }
      const start = new Date(backfillStartDate);
      const end = new Date(backfillEndDate);
      if (start > end) {
        MySwal.fire({ title: "Error", text: "Start date cannot be after end date.", icon: "error", confirmButtonText: "OK" });
        return;
      }
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 31) {
        MySwal.fire({ title: "Error", text: "Maximum date range can be of 1 month.", icon: "error", confirmButtonText: "OK" });
        return;
      }
    }

    let backfillData: any = {};
    if (backfillPeriod === "1 Day") {
      backfillData.days = 1;
    } else if (backfillPeriod === "1 Week") {
      backfillData.weeks = 1;
    } else if (backfillPeriod === "1 Month") {
      backfillData.months = 1;
    } else if (backfillPeriod === "Custom") {
      backfillData.start_date = backfillStartDate;
      backfillData.end_date = backfillEndDate;
    }

    const payload = {
      configuration: {
        backfill: backfillData
      }
    };

    localStorage.setItem("emailBackfillConfig", JSON.stringify(payload));

    setShowBackfillModal(false);

    try {
      const integrationEndpoint = backfillIntegrationType.toLowerCase();
      const response = await fetch(
        `${API_BASE_URL}/mail/${integrationEndpoint}/auth/?user_id=${user_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${localStorage.getItem("authCred")}`,
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        } else {
          MySwal.fire({
            title: "Success!",
            text: `${backfillIntegrationType === "microsoft" ? "Outlook" : "Google"} Mail integration connected successfully!`,
            icon: "success",
            confirmButtonText: "OK",
          });
          await fetchEmailProviders();
        }
      } else {
        MySwal.fire({
          title: "Error!",
          text: `Failed to connect ${backfillIntegrationType === "microsoft" ? "Outlook" : "Google"} Mail integration.`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: `An error occurred while connecting ${backfillIntegrationType === "microsoft" ? "Outlook" : "Google"} Mail integration.`,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleBrandIntegrationConnect = async (brand: "TikTok" | "Lazada" | "Shopee") => {
    setBrandAuthLoading(true);
    localStorage.setItem("last_auth_provider", brand);
    try {
      const endpoint = brand === "TikTok" ? "tiktok" : brand === "Lazada" ? "lazada" : "shopee";
      const response = await fetch(`${API_BASE_URL}/${endpoint}/auth/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        } else {
          MySwal.fire({
            title: "Error",
            text: `Failed to get auth URL for ${brand}.`,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } else {
        MySwal.fire({
          title: "Error",
          text: `Failed to connect ${brand} integration.`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: `An error occurred while connecting ${brand} integration.`,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setBrandAuthLoading(false);
    }
  };

  const handleGoogleChatConnect = async () => {
    setBrandAuthLoading(true);
    localStorage.setItem("last_auth_provider", "GoogleChat");
    try {
      const response = await fetch(`${API_BASE_URL}/google-chat/auth/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        } else {
          MySwal.fire({
            title: "Error",
            text: "Failed to get auth URL for Google Chat.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } else {
        MySwal.fire({
          title: "Error",
          text: "Failed to connect Google Chat.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      MySwal.fire({
        title: "Error!",
        text: "An error occurred while connecting Google Chat.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setBrandAuthLoading(false);
    }
  };



  const handleReintegrate = (chatbot: ChatBot) => {
    const platformKey = chatbot.external_chat_platform?.external_chat_platform_key;
    if (platformKey === "LAZADA") {
      setActiveBrandIntegration("Lazada");
    } else if (platformKey === "TIKTOK") {
      setActiveBrandIntegration("TikTok");
    } else if (platformKey === "SHOPEE") {
      setActiveBrandIntegration("Shopee");
    } else if (platformKey === "GOOGLE_CHAT") {
      handleGoogleChatConnect();
    } else {
      openEditChatbotModal(chatbot);
    }
  };

  const hasAuthorizedGoogleChatBot = chatbots.some(
    (bot) =>
      bot.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" &&
      (bot.chat_type?.chat_type_key === "INTERNAL" || (bot as any).chat_type_key === "INTERNAL") &&
      bot.is_authorized !== false
  );

  const EngatiIcon = () => {
    return (
      <img src="/assets/img/platform-icons/Engati.png" alt="Engati" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
    );
  };
  const LazadaIcon = () => {
    return (
      <img src="/assets/img/platform-icons/Lazada.png" alt="Lazada" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
    );
  };

  const TikTokIcon = () => {
    return (
      <img src="/assets/img/platform-icons/TikTok.png" alt="TikTok" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
    );
  };
  const ShopeeIcon = () => {
    return (
      <img src="/assets/img/platform-icons/Shopee.png" alt="Shopee" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
    );
  };
  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="integration"
                    variant="bottom"
                    includeIcons={true}
                    context="profile"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  {/* Settings Sidebar */}
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <h4 className="fw-semibold mb-3">
                          Integration Settings
                        </h4>
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link
                            to={route.emailSettings}
                            className={`fw-medium ${activeTab === "email" ? "active" : ""
                              }`}
                          >
                            Email Settings
                          </Link>
                          {(userRole?.role_key === "CLIENTADMIN" || userRole?.role_key === "SALESUSER") && (
                            <Link
                              to={route.chatSettings}
                              className={`fw-medium ${activeTab === "chatbot" ? "active" : ""
                                }`}
                            >
                              Chat Settings
                            </Link>
                          )}
                          {(userRole?.role_key === "CLIENTADMIN" || userRole?.role_key === "SALESUSER") && (
                            <Link
                              to={route.calendarSettings}
                              className={`fw-medium ${activeTab === "calendar" ? "active" : ""
                                }`}
                            >
                              Calendar Settings
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Settings Sidebar */}
                </div>
                <div className="col-xl-9 col-lg-12">
                  {/* Settings Info */}
                  <div className="card">
                    <div className="card-body">
                      {activeTab === "allocation" && <TeamAllocationSettings />}
                      {activeTab === "calendar" && <CalendarSettings />}
                      {activeTab === "email" && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h4 className="fs-20">{emailSettingsTab === "auto-lead-creation" ? "Auto Creation Rules" : "Email Settings"}</h4>
                          </div>

                          <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center ">
                            <button
                              className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${emailSettingsTab === "email-settings"
                                ? "btn-primary text-white shadow-sm"
                                : "btn-outline-light text-muted border-light-subtle"
                                }`}
                              onClick={() => {
                                setEmailSettingsTab("email-settings");
                                navigate(route.emailSettings);
                              }}
                              type="button"
                              style={{
                                minWidth: "fit-content",
                                fontSize: "0.875rem",
                                fontWeight:
                                  emailSettingsTab === "email-settings" ? "600" : "500",
                                transform:
                                  emailSettingsTab === "email-settings"
                                    ? "translateY(-1px)"
                                    : "none",
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <i className="ti ti-mail me-2"></i>
                              Email Settings
                            </button>
                            <button
                              className={`btn btn-sm d-flex align-items-center px-4 py-2 rounded-pill transition-all ${emailSettingsTab === "auto-lead-creation"
                                ? "btn-primary text-white shadow-sm"
                                : "btn-outline-light text-muted border-light-subtle"
                                }`}
                              onClick={() => {
                                setEmailSettingsTab("auto-lead-creation");
                                navigate(route.autoCreationRules);
                              }}
                              type="button"
                              style={{
                                minWidth: "fit-content",
                                fontSize: "0.875rem",
                                fontWeight:
                                  emailSettingsTab === "auto-lead-creation" ? "600" : "500",
                                transform:
                                  emailSettingsTab === "auto-lead-creation"
                                    ? "translateY(-1px)"
                                    : "none",
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <i className="ti ti-user-plus me-2"></i>
                              Auto Creation Rules
                            </button>
                          </div>

                          {emailSettingsTab === "email-settings" && (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="border rounded p-3 mb-4">
                                  <div className="row gy-3">
                                    <div className="col-sm-5">
                                      <div className="d-flex align-items-center">
                                        <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                          <i
                                            className="ti ti-mail text-danger"
                                            style={{ fontSize: "24px" }}
                                          ></i>
                                        </span>
                                        <div>
                                          <h6 className="fw-medium mb-1">
                                            Google Mail
                                          </h6>
                                          {loading ? (
                                            <span className="badge bg-soft-secondary text-secondary">
                                              Loading...
                                            </span>
                                          ) : getProviderStatus("GOOGLE")
                                            ?.connected ? (
                                            <span className="badge bg-soft-success text-success">
                                              Connected
                                            </span>
                                          ) : (
                                            <span className="badge bg-soft-purple text-purple">
                                              Not Connected
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-sm-7">
                                      <div className="d-flex align-items-end justify-content-end">
                                        {!loading &&
                                          !getProviderStatus("GOOGLE")
                                            ?.connected && (
                                            <Link
                                              to="#"
                                              className="btn btn-primary"
                                              onClick={() =>
                                                handleIntegrationConnect("Google")
                                              }
                                            >
                                              <i className="ti ti-plug-connected me-1" />
                                              Connect Now
                                            </Link>
                                          )}
                                        {!loading &&
                                          getProviderStatus("GOOGLE")
                                            ?.connected && (
                                            <div className="status-toggle">
                                              <input
                                                type="checkbox"
                                                id="mail1"
                                                className="check"
                                                checked={true}
                                                onChange={() =>
                                                  handleUnlinkAccount(
                                                    "GOOGLE",
                                                    "Google Mail"
                                                  )
                                                }
                                              />
                                              <label
                                                htmlFor="mail1"
                                                className="checktoggle"
                                              >
                                                {" "}
                                              </label>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="border rounded p-3 mb-4">
                                  <div className="row gy-3">
                                    <div className="col-sm-5">
                                      <div className="d-flex align-items-center">
                                        <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                          <i
                                            className="ti ti-brand-windows text-primary"
                                            style={{ fontSize: "24px" }}
                                          ></i>
                                        </span>
                                        <div>
                                          <h6 className="fw-medium mb-1">
                                            Outlook Mail
                                          </h6>
                                          {loading ? (
                                            <span className="badge bg-soft-secondary text-secondary">
                                              Loading...
                                            </span>
                                          ) : getProviderStatus("MICROSOFT_365")
                                            ?.connected ? (
                                            <span className="badge bg-soft-success text-success">
                                              Connected
                                            </span>
                                          ) : (
                                            <span className="badge bg-soft-purple text-purple">
                                              Not Connected
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-sm-7">
                                      <div className="d-flex align-items-end justify-content-end">
                                        {!loading &&
                                          !getProviderStatus("MICROSOFT_365")
                                            ?.connected && (
                                            <Link
                                              to="#"
                                              className="btn btn-primary"
                                              onClick={() =>
                                                handleIntegrationConnect(
                                                  "microsoft"
                                                )
                                              }
                                            >
                                              <i className="ti ti-plug-connected me-1" />
                                              Connect Now
                                            </Link>
                                          )}
                                        {!loading &&
                                          getProviderStatus("MICROSOFT_365")
                                            ?.connected && (
                                            <div className="status-toggle">
                                              <input
                                                type="checkbox"
                                                id="mail2"
                                                className="check"
                                                checked={true}
                                                onChange={() =>
                                                  handleUnlinkAccount(
                                                    "MICROSOFT_365",
                                                    "Outlook Mail"
                                                  )
                                                }
                                              />
                                              <label
                                                htmlFor="mail2"
                                                className="checktoggle"
                                              >
                                                {" "}
                                              </label>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {showBackfillModal && (
                                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={(e) => { if (e.target === e.currentTarget) { setShowBackfillModal(false); } }}>
                                  <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '1rem', overflow: 'hidden' }}>
                                      <div className="modal-header border-bottom-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-start">
                                        <div className="d-flex align-items-center">
                                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '32px', height: '32px', border: `1px solid ${backfillIntegrationType === "microsoft" ? "#0d6efd" : "#dc3545"}` }}>
                                            <i className={backfillIntegrationType === "microsoft" ? "ti ti-brand-windows text-primary" : "ti ti-mail text-danger"} style={{ fontSize: '20px' }}></i>
                                          </div>
                                          <div>
                                            <h4 className="modal-title fw-bold mb-1" style={{ color: '#2b3445', fontSize: '1.25rem' }}>
                                              Connect {backfillIntegrationType === "microsoft" ? "Outlook" : "Google"} Mail
                                            </h4>
                                          </div>
                                        </div>
                                        <button type="button" className="btn-close shadow-none" onClick={() => setShowBackfillModal(false)}></button>
                                      </div>
                                      <div className="modal-body p-4">
                                        <div className="mb-4">
                                          <label className="form-label fw-semibold text-dark d-flex align-items-center mb-2">
                                            Backfill Period
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip id="backfill-tooltip">
                                                  Backfilling imports your previous emails up to the selected range.
                                                </Tooltip>
                                              }
                                            >
                                              <i className="ti ti-info-circle ms-2 cursor-pointer text-muted"></i>
                                            </OverlayTrigger>
                                          </label>
                                          <select
                                            className="form-select form-select-lg cursor-pointer bg-light border-0 shadow-sm"
                                            value={backfillPeriod}
                                            onChange={(e) => setBackfillPeriod(e.target.value)}
                                            style={{ borderRadius: '0.75rem', fontSize: '0.95rem', height: '50px' }}
                                          >
                                            <option value="1 Day">1 Day</option>
                                            <option value="1 Week">1 Week</option>
                                            <option value="1 Month">1 Month</option>
                                            <option value="Custom">Custom Range</option>
                                          </select>
                                        </div>
                                        {backfillPeriod === "Custom" && (
                                          <div className="mb-2">
                                            <label className="form-label fw-semibold text-dark mb-2">Select Date Range <span className="text-muted fw-normal" style={{ fontSize: '0.8rem' }}>(Max 1 Month)</span></label>
                                            <div className="icon-form">
                                              <span className="form-icon">
                                                <i className="ti ti-calendar position-relative" style={{ fontSize: '18px', color: '#6c757d', marginTop: '13px' }} />
                                              </span>
                                              <DateRangePicker
                                                initialSettings={{
                                                  autoUpdateInput: false,
                                                  maxSpan: { days: 30 },
                                                  opens: "center",
                                                  drops: "up",
                                                  maxDate: new Date()
                                                }}
                                                onApply={(event: any, picker: any) => {
                                                  setBackfillStartDate(picker.startDate.format("YYYY-MM-DD"));
                                                  setBackfillEndDate(picker.endDate.format("YYYY-MM-DD"));
                                                }}
                                              >
                                                <input
                                                  className="form-control form-control-lg bg-light border-0 shadow-sm cursor-pointer"
                                                  type="text"
                                                  placeholder="Click to select date range"
                                                  value={backfillStartDate && backfillEndDate ? `${formatCustomDateTime(backfillStartDate, true)} - ${formatCustomDateTime(backfillEndDate, true)}` : ""}
                                                  readOnly
                                                  style={{ paddingLeft: "42px", borderRadius: '0.75rem', fontSize: '0.95rem', height: '50px' }}
                                                />
                                              </DateRangePicker>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="modal-footer border-top-0 pt-0 px-4 pb-4 d-flex justify-content-center gap-3">
                                        <button type="button" className="btn btn-outline-primary fw-medium rounded-pill px-4 shadow-sm d-flex align-items-center" onClick={() => setShowBackfillModal(false)} style={{ height: '46px' }}>
                                          <i className="bi bi-x-lg me-2"></i>Cancel
                                        </button>
                                        <button type="button" className="btn btn-primary fw-medium rounded-pill px-4 shadow-sm d-flex align-items-center" onClick={proceedWithEmailIntegration} style={{ height: '46px' }}>
                                          <i className="ti ti-plug-connected me-2" style={{ fontSize: '18px' }}></i>Connect {backfillIntegrationType === "microsoft" ? "Outlook" : "Gmail"}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {emailSettingsTab === "auto-lead-creation" && (
                            <div className="row">
                              <div className="col-md-12">
                                <AutoCreationRules />
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {activeTab === "social" && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h4 className="fs-20">Social Media Settings</h4>
                          </div>
                          <div className="row">
                            <div className="col-md-12">
                              <div className="border rounded p-3 mb-4">
                                <div className="row gy-3">
                                  <div className="col-sm-5">
                                    <div className="d-flex align-items-center">
                                      <span className="avatar avtar-lg border me-2 flex-shrink-0">
                                        <i
                                          className="ti ti-brand-line text-success"
                                          style={{ fontSize: "24px" }}
                                        ></i>
                                      </span>
                                      <div>
                                        <h6 className="fw-medium mb-1">LINE</h6>
                                        {socialLoading ? (
                                          <span className="badge bg-soft-secondary text-secondary">
                                            Loading...
                                          </span>
                                        ) : getSocialProviderStatus("LINE")
                                          ?.connected ? (
                                          <span className="badge bg-soft-success text-success">
                                            Connected
                                          </span>
                                        ) : (
                                          <span className="badge bg-soft-purple text-purple">
                                            Not Connected
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-sm-7">
                                    <div className="d-flex align-items-end justify-content-end">
                                      {!socialLoading &&
                                        !getSocialProviderStatus("LINE")
                                          ?.connected && (
                                          <Link
                                            to="#"
                                            className="btn btn-primary"
                                            onClick={() =>
                                              handleIntegrationConnect("LINE")
                                            }
                                          >
                                            <i className="ti ti-plug-connected me-1" />
                                            Connect Now
                                          </Link>
                                        )}
                                      {!socialLoading &&
                                        getSocialProviderStatus("LINE")
                                          ?.connected && (
                                          <div className="status-toggle">
                                            <input
                                              type="checkbox"
                                              id="line1"
                                              className="check"
                                              checked={true}
                                              onChange={() =>
                                                handleSocialUnlinkAccount(
                                                  "LINE",
                                                  "LINE"
                                                )
                                              }
                                            />
                                            <label
                                              htmlFor="line1"
                                              className="checktoggle"
                                            >
                                              {" "}
                                            </label>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {activeTab === "chatbot" && (userRole?.role_key === "CLIENTADMIN" || userRole?.role_key === "SALESUSER") && (
                        <>
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h4 className="fs-20">Chat Settings</h4>
                          </div>

                          {userRole?.role_key === "CLIENTADMIN" && !selectedBotDetail && (
                            <div className="d-flex flex-wrap gap-1 mb-4 justify-content-center">
                              <button
                                className={`btn btn-sm d-flex align-items-center px-2 py-1 rounded-pill transition-all ${chatSettingsTab === "chatbot"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setChatSettingsTab("chatbot")}
                                type="button"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: chatSettingsTab === "chatbot" ? "600" : "500",
                                  transform: chatSettingsTab === "chatbot" ? "translateY(-1px)" : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i className="ti ti-message-chatbot me-1" style={{ fontSize: "1rem" }}></i>
                                Chatbot Settings
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center px-2 py-1 rounded-pill transition-all ${chatSettingsTab === "status"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setChatSettingsTab("status")}
                                type="button"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: chatSettingsTab === "status" ? "600" : "500",
                                  transform: chatSettingsTab === "status" ? "translateY(-1px)" : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i className="ti ti-list-check me-1" style={{ fontSize: "1rem" }}></i>
                                Status Settings
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center px-2 py-1 rounded-pill transition-all ${chatSettingsTab === "privacy"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setChatSettingsTab("privacy")}
                                type="button"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: chatSettingsTab === "privacy" ? "600" : "500",
                                  transform: chatSettingsTab === "privacy" ? "translateY(-1px)" : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i className="ti ti-eye me-1" style={{ fontSize: "1rem" }}></i>
                                Privacy Settings
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center px-2 py-1 rounded-pill transition-all ${chatSettingsTab === "team"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setChatSettingsTab("team")}
                                type="button"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: chatSettingsTab === "team" ? "600" : "500",
                                  transform: chatSettingsTab === "team" ? "translateY(-1px)" : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i className="ti ti-users-group me-1" style={{ fontSize: "1rem" }}></i>
                                Team Allocation
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center px-2 py-1 rounded-pill transition-all ${chatSettingsTab === "template"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setChatSettingsTab("template")}
                                type="button"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: chatSettingsTab === "template" ? "600" : "500",
                                  transform: chatSettingsTab === "template" ? "translateY(-1px)" : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i className="ti ti-template me-1" style={{ fontSize: "1rem" }}></i>
                                Chat Template
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center px-2 py-1 rounded-pill transition-all ${chatSettingsTab === "autoreply"
                                  ? "btn-primary text-white shadow-sm"
                                  : "btn-outline-light text-muted border-light-subtle"
                                  }`}
                                onClick={() => setChatSettingsTab("autoreply")}
                                type="button"
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: chatSettingsTab === "autoreply" ? "600" : "500",
                                  transform: chatSettingsTab === "autoreply" ? "translateY(-1px)" : "none",
                                  transition: "all 0.2s ease-in-out",
                                }}
                              >
                                <i className="ti ti-clock-hour-4 me-1" style={{ fontSize: "1rem" }}></i>
                                Auto-Reply
                              </button>
                            </div>
                          )}

                          <div className="tab-content">
                            {chatSettingsTab === "chatbot" && (
                              <div className="tab-pane fade show active">
                                <>
                                  <div className="d-flex justify-content-between mb-3 align-items-center">
                                    {!selectedBotDetail && (
                                      <div className="ms-auto">
                                        <button
                                          type="button"
                                          className="btn btn-primary"
                                          onClick={() => setShowIntegrationSelectionModal(true)}
                                        >
                                          <i className="fa fa-plus me-1"></i>
                                          Add Chatbot
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  {chatbotLoading ? (
                                    <div
                                      className="d-flex justify-content-center align-items-center"
                                      style={{ minHeight: "200px" }}
                                    >
                                      <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                      </div>
                                    </div>
                                  ) : selectedBotDetail ? (
                                    <div className="bot-detail-view px-2">
                                      <div className="d-flex align-items-center mb-4">
                                        <button
                                          type="button"
                                          className="btn me-3 border-0 bg-transparent text-primary"
                                          onClick={() => setSelectedBotDetail(null)}
                                          style={{ fontSize: "1.2rem" }}
                                        >
                                          <i className="fa fa-arrow-left"></i>
                                        </button>
                                        <h4 className="mb-0 fw-bold">{selectedBotDetail.bot_name}</h4>
                                      </div>

                                      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
                                        <div className="card-header bg-transparent border-bottom-0 pt-4 px-4">
                                          <h5 className="card-title mb-0 fw-bold text-dark">Bot Settings</h5>
                                        </div>
                                        <div className="card-body px-4 pb-4">
                                          <div className="row g-3">
                                            {selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" ? (
                                              <>
                                                <div className="col-md-4">
                                                  <label className="form-label">Bot Name <span className="text-danger">*</span></label>
                                                  <input
                                                    type="text"
                                                    className={`form-control`}
                                                    placeholder="Enter Bot Name"
                                                    value={selectedBotDetail.bot_name}
                                                    onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_name: e.target.value })}

                                                  />
                                                </div>
                                                <div className="col-md-4">
                                                  <label className="form-label">Seller ID</label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    value={selectedBotDetail.seller_id || ""}
                                                    readOnly
                                                    disabled
                                                    style={{ backgroundColor: "#f8f9fa" }}
                                                  />
                                                </div>
                                                <div className="col-md-4">
                                                  <label className="form-label">Seller Email</label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    value={selectedBotDetail.seller_email || ""}
                                                    readOnly
                                                    disabled
                                                    style={{ backgroundColor: "#f8f9fa" }}
                                                  />
                                                </div>
                                              </>
                                            ) : selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL") ? (
                                              <>
                                                <div className="col-md-4">
                                                  <label className="form-label">Bot Name <span className="text-danger">*</span></label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot Name"
                                                    value={selectedBotDetail.bot_name}
                                                    onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_name: e.target.value })}
                                                  />
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                <div className="col-md-4">
                                                  <label className="form-label">Bot Name <span className="text-danger">*</span></label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot Name"
                                                    value={selectedBotDetail.bot_name}
                                                    onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_name: e.target.value })}
                                                  />
                                                </div>
                                                <div className="col-md-4">
                                                  <label className="form-label">Bot ID <span className="text-danger">*</span></label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot ID"
                                                    value={selectedBotDetail.bot_id}
                                                    onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_id: e.target.value })}
                                                  />
                                                </div>
                                                {userRole?.role_key !== "SALESUSER" && (
                                                  <div className="col-md-4">
                                                    <label className="form-label">Bot Key <span className="text-danger">*</span></label>
                                                    <input
                                                      type="text"
                                                      className="form-control"
                                                      placeholder="Enter Bot Key"
                                                      value={selectedBotDetail.bot_key || ""}
                                                      onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_key: e.target.value })}
                                                    />
                                                  </div>
                                                )}
                                                {userRole?.role_key !== "SALESUSER" && (
                                                  <div className="col-md-4">
                                                    <label className="form-label">Webhook URL <span className="text-danger">*</span></label>
                                                    <input
                                                      type="text"
                                                      className="form-control"
                                                      placeholder="Enter Webhook URL"
                                                      value={selectedBotDetail.bot_message_webhook_url}
                                                      onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_message_webhook_url: e.target.value })}
                                                    />
                                                  </div>
                                                )}
                                                {userRole?.role_key !== "SALESUSER" && (
                                                  <div className="col-md-4">
                                                    <label className="form-label">Bot Customer ID <span className="text-danger">*</span></label>
                                                    <input
                                                      type="text"
                                                      className="form-control"
                                                      placeholder="Enter Bot Customer ID"
                                                      value={selectedBotDetail.bot_customer_id || ""}
                                                      onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_customer_id: e.target.value })}
                                                    />
                                                  </div>
                                                )}
                                                {userRole?.role_key !== "SALESUSER" && (
                                                  <div className="col-md-4">
                                                    <label className="form-label">Bot API Key <span className="text-danger">*</span></label>
                                                    <input
                                                      type="text"
                                                      className="form-control"
                                                      placeholder="Enter Bot API Key"
                                                      value={selectedBotDetail.bot_api_key || ""}
                                                      onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_api_key: e.target.value })}
                                                    />
                                                  </div>
                                                )}
                                                {userRole?.role_key !== "SALESUSER" && (
                                                  <div className="col-md-4">
                                                    <label className="form-label d-flex align-items-center">
                                                      Bot Flow Key
                                                      <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                          <Tooltip id="bot-flow-key-tooltip-detail">
                                                            Enter the Flow Key of the path that triggers the <strong>Transfer to Agent</strong> node in your Engati bot flow to enable CRM to trigger chat handoff from the bot to a real agent.
                                                          </Tooltip>
                                                        }
                                                      >
                                                        <i className="fa fa-info-circle ms-2 text-muted" style={{ cursor: "pointer" }}></i>
                                                      </OverlayTrigger>
                                                    </label>
                                                    <input
                                                      type="text"
                                                      className="form-control"
                                                      placeholder="Enter Bot Flow Key"
                                                      value={selectedBotDetail.bot_flow_key || ""}
                                                      onChange={(e) => setSelectedBotDetail({ ...selectedBotDetail, bot_flow_key: e.target.value })}
                                                    />
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                          {!(userRole?.role_key === "SALESUSER" && !(selectedBotDetail?.chat_type?.chat_type_key === "INTERNAL" || (selectedBotDetail as any)?.chat_type_key === "INTERNAL")) && (
                                            <div className="d-flex justify-content-end mt-4">
                                              <button className="btn btn-primary px-4 rounded-pill" onClick={handleSaveBotSettings} disabled={chatbotLoading}>
                                                {chatbotLoading ? "Saving..." : "Save Bot Settings"}
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
                                        <div className="card-header bg-transparent border-bottom-0 pt-4 px-4">
                                          <h5 className="card-title mb-0 fw-bold text-dark">
                                            {(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" || (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL")))
                                              ? "Account Settings"
                                              : "Channel Account Settings"}
                                          </h5>
                                        </div>
                                        <div className="card-body px-4 pb-4">
                                          {channelAccountLoading ? (
                                            <div className="text-center py-4">
                                              <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                            </div>
                                          ) : (
                                            <>
                                              {botChannelAccounts.length > 0 && (
                                                <div className="mb-4">
                                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 className="text-muted mb-0">Connected {selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL") ? "Accounts" : "Channels"}</h6>
                                                    {!(userRole?.role_key === "SALESUSER" && !(selectedBotDetail?.chat_type?.chat_type_key === "INTERNAL" || (selectedBotDetail as any)?.chat_type_key === "INTERNAL")) &&
                                                      !(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" || (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL"))) &&
                                                      availableChannelOptions.length > 0 && (
                                                        <button
                                                          className="btn btn-sm btn-primary"
                                                          onClick={() => {
                                                            setEditingChannelAccount(null);
                                                            setChannelAccountFormData({ account_name: "", external_chat_channel_id: "", is_visible_to_tenant: false });
                                                            setShowChannelAccountModal(true);
                                                          }}
                                                        >
                                                          <i className="fa fa-plus me-1"></i> Add Account
                                                        </button>
                                                      )}
                                                  </div>
                                                  <div className="row g-2">
                                                    {botChannelAccounts.map((account) => (
                                                      <div key={account.external_chat_bot_channel_account_id} className="col-md-6">
                                                        <div
                                                          className="d-flex align-items-center justify-content-between p-3 rounded"
                                                          style={{
                                                            backgroundColor: getChannelBgColor(account.external_chat_channel?.external_chat_channel_key || ""),
                                                            border: `1px solid ${getChannelColor(account.external_chat_channel?.external_chat_channel_key || "")}20`,
                                                          }}
                                                        >
                                                          <div className="d-flex align-items-center">
                                                            {selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" ? (
                                                              <div style={{ width: "24px", height: "24px", marginRight: "10px", display: "flex", alignItems: "center" }}><TikTokIcon /></div>
                                                            ) : selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" ? (
                                                              <div style={{ width: "24px", height: "24px", marginRight: "10px", display: "flex", alignItems: "center" }}><LazadaIcon /></div>
                                                            ) : selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" ? (
                                                              <div style={{ width: "24px", height: "24px", marginRight: "10px", display: "flex", alignItems: "center" }}><ShopeeIcon /></div>
                                                            ) : selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL") ? (
                                                              <div style={{ width: "24px", height: "24px", marginRight: "10px", display: "flex", alignItems: "center" }}><img src="/assets/img/platform-icons/Google-Chat.png" alt="Google Chat" style={{ width: "24px", height: "24px", objectFit: "contain" }} /></div>
                                                            ) : (
                                                              <i
                                                                className={getChannelIcon(account.external_chat_channel?.external_chat_channel_key || "")}
                                                                style={{ color: getChannelColor(account.external_chat_channel?.external_chat_channel_key || ""), fontSize: "1.2rem", marginRight: "10px" }}
                                                              ></i>
                                                            )}
                                                            <div>
                                                              <div className="fw-semibold">{account.account_name}</div>
                                                              {(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE") && (account.shop_code || account.shop_id) && (
                                                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>{account.shop_code || account.shop_id}</div>
                                                              )}
                                                            </div>
                                                          </div>
                                                          {!(userRole?.role_key === "SALESUSER" && !(selectedBotDetail?.chat_type?.chat_type_key === "INTERNAL" || (selectedBotDetail as any)?.chat_type_key === "INTERNAL")) && (
                                                            <button
                                                              type="button"
                                                              className="btn btn-sm btn-link text-primary p-0"
                                                              onClick={() => handleEditChannelAccount(account)}
                                                            >
                                                              <i className="ti ti-edit"></i>
                                                            </button>
                                                          )}
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}

                                              {botChannelAccounts.length === 0 && !(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" || (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL"))) && (
                                                <div className="text-center text-muted py-5">
                                                  <i className="ti ti-robot-off mb-3" style={{ fontSize: "48px", color: "#ccc" }}></i>
                                                  <p className="mb-3 mt-2">No connected accounts found.</p>
                                                  {!(userRole?.role_key === "SALESUSER" && !(selectedBotDetail?.chat_type?.chat_type_key === "INTERNAL" || (selectedBotDetail as any)?.chat_type_key === "INTERNAL")) && availableChannelOptions.length > 0 && (
                                                    <button className="btn btn-primary" onClick={() => {
                                                      setEditingChannelAccount(null);
                                                      setChannelAccountFormData({ account_name: "", external_chat_channel_id: "", is_visible_to_tenant: false });
                                                      setShowChannelAccountModal(true);
                                                    }}>
                                                      <i className="fa fa-plus me-1"></i> Add Account
                                                    </button>
                                                  )}
                                                </div>
                                              )}

                                              {availableChannelOptions.length === 0 && botChannelAccounts.length > 0 && !(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" || (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL"))) && (
                                                <div className="text-center text-muted py-3">
                                                  <i className="ti ti-check-circle" style={{ fontSize: "1.5rem", color: "#22c55e" }}></i>
                                                  <p className="mb-0 mt-2">All available channels have been connected.</p>
                                                </div>
                                              )}

                                              {botChannelAccounts.length === 0 && (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" || (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL"))) && (
                                                <div className="text-center text-muted py-3">
                                                  <i className="ti ti-robot-off" style={{ fontSize: "1.5rem", color: "#ccc" }}></i>
                                                  <p className="mb-0 mt-2">No any connected account found</p>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ) : chatbots.length === 0 ? (
                                    <div className="text-center py-5">
                                      <i className="ti ti-robot-off" style={{ fontSize: "48px", color: "#ccc" }}></i>
                                      <p className="text-muted mt-3">No chatbots configured yet.</p>
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => setShowIntegrationSelectionModal(true)}
                                      >
                                        Add Your First Chatbot
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="row">
                                      {chatbots
                                        .filter((chatbot) => {
                                          if (chatbot.is_deleted) return false;
                                          const isInternal = chatbot?.chat_type?.chat_type_key === "INTERNAL" ||
                                            (chatbot as any).chat_type_key === "INTERNAL";
                                          if (isInternal) {
                                            return chatbot.created_by === auth?.user_id;
                                          }
                                          return true;
                                        })
                                        .map((chatbot) => (
                                          <div key={chatbot.external_chat_tenant_bot_id} className="col-md-4 mb-3">
                                            <div
                                              className="card border-0 position-relative h-100"
                                              style={{
                                                borderRadius: "12px",
                                                boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                                                transition: "all 0.3s ease",
                                                overflow: "hidden",
                                                opacity: chatbot.is_authorized === false ? 0.65 : 1,
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-5px)";
                                                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.08)";
                                              }}
                                            >
                                              <div
                                                style={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: 0,
                                                  padding: '2px 8px',
                                                  fontSize: '0.65rem',
                                                  fontWeight: 600,
                                                  borderBottomRightRadius: '8px',
                                                  backgroundColor: chatbot.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" || (chatbot as any).chat_type_key === "INTERNAL" || (chatbot as any).chat_type?.chat_type_key === "INTERNAL" ? 'rgba(34, 197, 94, 0.15)' : 'rgba(130, 78, 235, 0.15)',
                                                  color: chatbot.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" || (chatbot as any).chat_type_key === "INTERNAL" || (chatbot as any).chat_type?.chat_type_key === "INTERNAL" ? '#16a34a' : '#824EEB',
                                                  zIndex: 1
                                                }}
                                              >
                                                {chatbot.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" || (chatbot as any).chat_type_key === "INTERNAL" || (chatbot as any).chat_type?.chat_type_key === "INTERNAL" ? "Internal Chat" : "External Chat"}
                                              </div>
                                              {chatbot.is_authorized === false && (
                                                <div style={{
                                                  backgroundColor: "#FFD70026",
                                                  borderRadius: "8px",
                                                  padding: "2px 8px",
                                                  position: 'absolute',
                                                  fontSize: "0.6rem",
                                                  top: 0,
                                                  right: 0,
                                                  zIndex: 1,
                                                }}>
                                                  <i className="ti ti-refresh text-warning me-1"></i>
                                                  <span className="text-warning ">Authorization Expired</span>
                                                </div>
                                              )}
                                              <div className="card-body py-3 px-3" onClick={() => chatbot.is_authorized !== false ? handleBotClick(chatbot) : undefined} style={{ cursor: chatbot.is_authorized === false ? "default" : "pointer" }}>
                                                <div className="d-flex justify-content-end align-items-center position-relative" style={{ minHeight: "24px", height: "24px" }}>
                                                  {!(userRole?.role_key === "SALESUSER" && !(chatbot?.chat_type?.chat_type_key === "INTERNAL" || (chatbot as any).chat_type_key === "INTERNAL")) && chatbot.is_authorized === true && (
                                                    <>
                                                      <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-muted d-flex align-items-center justify-content-center"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setActiveDropdown(
                                                            activeDropdown === chatbot.external_chat_tenant_bot_id
                                                              ? null
                                                              : chatbot.external_chat_tenant_bot_id
                                                          );
                                                        }}
                                                        style={{ fontSize: "1.1rem", lineHeight: 1, width: "24px", height: "24px" }}
                                                      >
                                                        <i className="ti ti-dots"></i>
                                                      </button>
                                                      {activeDropdown === chatbot.external_chat_tenant_bot_id && (
                                                        <div
                                                          className="dropdown-menu show position-absolute"
                                                          style={{
                                                            top: "100%",
                                                            right: 0,
                                                            minWidth: "120px",
                                                            zIndex: 1000,
                                                          }}
                                                        >
                                                          <button
                                                            type="button"
                                                            className="dropdown-item text-danger"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteChatbot(chatbot); }}
                                                          >
                                                            <i className="ti ti-trash me-2"></i>
                                                            Delete
                                                          </button>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                                <div className="text-center">
                                                  <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                      <Tooltip id={`tooltip-${chatbot.bot_id}`}>
                                                        {chatbot.external_chat_platform?.external_chat_platform_key === "ENGATI" || chatbot.external_chat_platform?.external_chat_platform_display_name === "Engati" ? "Cloudee Chat Bot" : (chatbot.external_chat_platform?.external_chat_platform_display_name || "Platform")}
                                                      </Tooltip>
                                                    }
                                                  >
                                                    <div
                                                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                                                      style={{
                                                        width: "50px",
                                                        height: "50px",
                                                        backgroundColor: "#ffffffff",
                                                      }}
                                                    >
                                                      {chatbot.external_chat_platform?.external_chat_platform_key === "TIKTOK" ? (
                                                        <TikTokIcon />
                                                      ) : chatbot.external_chat_platform?.external_chat_platform_key === "LAZADA" ? (
                                                        <LazadaIcon />
                                                      ) : chatbot.external_chat_platform?.external_chat_platform_key === "SHOPEE" ? (
                                                        <ShopeeIcon />
                                                      ) : chatbot.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((chatbot as any).chat_type_key === "INTERNAL" || (chatbot as any).chat_type?.chat_type_key === "INTERNAL") ? (
                                                        <img src="/assets/img/platform-icons/Google-Chat.png" alt="Google Chat" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
                                                      ) : (
                                                        <EngatiIcon />
                                                      )}
                                                    </div>
                                                  </OverlayTrigger>
                                                  <h6 className="fw-semibold mb-1" style={{ fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {chatbot.bot_name ? (chatbot.bot_name.length > 20 ? `${chatbot.bot_name.substring(0, 20)}...` : chatbot.bot_name) : "Chatbot"}
                                                  </h6>
                                                  <small className="text-muted d-block text-truncate" style={{ fontSize: "0.75rem", minHeight: "18px" }}>
                                                    {chatbot.external_chat_platform?.external_chat_platform_key === "TIKTOK" || chatbot.external_chat_platform?.external_chat_platform_key === "LAZADA" || chatbot.external_chat_platform?.external_chat_platform_key === "SHOPEE" ? (chatbot.seller_id || "\u00A0") : (chatbot.bot_id || "\u00A0")}
                                                  </small>
                                                  {chatbot.is_authorized === false && (
                                                    <div className="border-top mt-1">
                                                      <button
                                                        type="button"
                                                        style={{ fontSize: "0.7rem" }}
                                                        className="btn btn-warning btn-sm mt-1 rounded-pill"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleReintegrate(chatbot);
                                                        }}
                                                        disabled={brandAuthLoading}
                                                      >
                                                        <i className="ti ti-refresh me-1"></i>
                                                        <span>Re-Authorize</span>
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  )}

                                  {showChannelAccountModal && selectedBotDetail && (
                                    <div
                                      className="modal fade show d-block"
                                      tabIndex={-1}
                                      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
                                      onClick={(e) => {
                                        if (e.target === e.currentTarget) {
                                          setShowChannelAccountModal(false);
                                          setEditingChannelAccount(null);
                                        }
                                      }}
                                    >
                                      <div className="modal-dialog modal-dialog-centered" style={{ minWidth: "30%" }}>
                                        <div className="modal-content">
                                          <div className="modal-header">
                                            <h5 className="modal-title">
                                              {editingChannelAccount ? "Edit " + (selectedBotDetail.external_chat_platform?.external_chat_platform_display_name || "Account") : "Add New " + (selectedBotDetail.external_chat_platform?.external_chat_platform_display_name || "Account")}
                                            </h5>
                                            <button
                                              type="button"
                                              className="btn-close"
                                              onClick={() => {
                                                setShowChannelAccountModal(false);
                                                setEditingChannelAccount(null);
                                              }}
                                            ></button>
                                          </div>
                                          <div className="modal-body">
                                            <div className="mb-3">
                                              <label className="form-label">Account Name <span className="text-danger">*</span></label>
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. My Channel Account"
                                                value={channelAccountFormData.account_name}
                                                onChange={(e) => setChannelAccountFormData({ ...channelAccountFormData, account_name: e.target.value })}
                                              />
                                            </div>
                                            {(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE") && (
                                              <div className="mb-3">
                                                <label className="form-label">Account Code</label>
                                                <input
                                                  type="text"
                                                  className="form-control"
                                                  value={editingChannelAccount?.shop_code || editingChannelAccount?.shop_id || ""}
                                                  readOnly
                                                  disabled
                                                  style={{ backgroundColor: "#f8f9fa" }}
                                                />
                                              </div>
                                            )}
                                            {!(selectedBotDetail.external_chat_platform?.external_chat_platform_key === "TIKTOK" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "LAZADA" || selectedBotDetail.external_chat_platform?.external_chat_platform_key === "SHOPEE" || (selectedBotDetail.external_chat_platform?.external_chat_platform_key === "GOOGLE_CHAT" && ((selectedBotDetail as any).chat_type_key === "INTERNAL" || (selectedBotDetail as any).chat_type?.chat_type_key === "INTERNAL"))) && (
                                              <div className="mb-3">
                                                <label className="form-label">Channel <span className="text-danger">*</span></label>
                                                <Select
                                                  classNamePrefix="react-select"
                                                  options={editingChannelAccount ? externalChatChannelOptions : availableChannelOptions}
                                                  value={externalChatChannelOptions.find(opt => opt.value === Number(channelAccountFormData.external_chat_channel_id))}
                                                  onChange={(opt: any) => setChannelAccountFormData({ ...channelAccountFormData, external_chat_channel_id: opt ? opt.value : "" })}
                                                  formatOptionLabel={formatChannelOption}
                                                  styles={{
                                                    ...customSelectStyles,
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                  }}
                                                  menuPortalTarget={document.body}
                                                  placeholder="Select Channel"
                                                  isClearable
                                                  isDisabled={!!editingChannelAccount}
                                                />
                                              </div>
                                            )}
                                          </div>
                                          <div className="modal-footer d-flex justify-content-between">
                                            <div>
                                              {editingChannelAccount && (
                                                <button
                                                  className="btn btn-outline-danger px-4 rounded-pill"
                                                  onClick={() => {
                                                    handleDeleteChannelAccount(editingChannelAccount);
                                                    setShowChannelAccountModal(false);
                                                  }}
                                                  disabled={channelAccountLoading}
                                                >
                                                  <i className="ti ti-trash me-1"></i> Delete
                                                </button>
                                              )}
                                            </div>
                                            <div className="d-flex gap-2">
                                              <button
                                                className="btn btn-outline-secondary px-4 rounded-pill"
                                                onClick={() => {
                                                  setShowChannelAccountModal(false);
                                                  setEditingChannelAccount(null);
                                                }}
                                              >
                                                <i className="ti ti-x me-2" />
                                                Cancel
                                              </button>
                                              <button className="btn btn-primary px-4 rounded-pill" onClick={handleSaveChannelAccount} disabled={channelAccountLoading}>
                                                <i className="ti ti-check me-2" />
                                                {channelAccountLoading ? "Saving..." : editingChannelAccount ? "Update" : "Add"}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {showIntegrationSelectionModal && (
                                    <div
                                      className="modal fade show d-block"
                                      tabIndex={-1}
                                      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                                      onClick={(e) => {
                                        if (e.target === e.currentTarget) setShowIntegrationSelectionModal(false);
                                      }}
                                    >
                                      <div className="modal-dialog modal-dialog-centered">
                                        <div className="modal-content">
                                          <div className="modal-header d-flex justify-content-between align-items-center">
                                            <h5 className="modal-title">Select Platform</h5>
                                            {userRole?.role_key !== "SALESUSER" && (
                                              <div className="d-flex align-items-center gap-2">
                                                <div
                                                  className="d-flex align-items-center position-relative"
                                                  style={{
                                                    backgroundColor: "#f1f5f9",
                                                    borderRadius: "50px",
                                                    padding: "3px",
                                                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                                                    display: "inline-flex",
                                                    width: "180px",
                                                    fontSize: "10px",
                                                  }}
                                                >
                                                  <div
                                                    className="position-absolute"
                                                    style={{
                                                      top: "4px",
                                                      bottom: "4px",
                                                      left: isInternalChat ? "4px" : "50%",
                                                      width: "calc(50% - 4px)",
                                                      backgroundColor: "#fff",
                                                      borderRadius: "50px",
                                                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                      transition: "all 0.35s cubic-bezier(0.4, 0.0, 0.2, 1)",
                                                      zIndex: 1
                                                    }}
                                                  />
                                                  <div
                                                    style={{
                                                      flex: 1,
                                                      textAlign: "center",
                                                      padding: "6px 0",
                                                      fontWeight: isInternalChat ? 600 : 500,
                                                      color: isInternalChat ? "#0f172a" : "#64748b",
                                                      cursor: "pointer",
                                                      fontSize: "0.7rem",
                                                      position: "relative",
                                                      zIndex: 2,
                                                      transition: "color 0.3s ease"
                                                    }}
                                                    onClick={() => setIsInternalChat(true)}
                                                  >
                                                    Internal Chat
                                                  </div>
                                                  <div
                                                    style={{
                                                      flex: 1,
                                                      textAlign: "center",
                                                      padding: "6px 0",
                                                      fontWeight: !isInternalChat ? 600 : 500,
                                                      color: !isInternalChat ? "#0f172a" : "#64748b",
                                                      cursor: "pointer",
                                                      fontSize: "0.7rem",
                                                      position: "relative",
                                                      zIndex: 2,
                                                      transition: "color 0.3s ease"
                                                    }}
                                                    onClick={() => setIsInternalChat(false)}
                                                  >
                                                    External Chat
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="modal-body">
                                            <div className="d-flex flex-column gap-3">
                                              {(userRole?.role_key === "SALESUSER" || isInternalChat) ? (
                                                hasAuthorizedGoogleChatBot ? (
                                                  <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip id="google-chat-disabled-tooltip">Google Chat already integrated</Tooltip>}
                                                  >
                                                    <span style={{ display: "block" }}>
                                                      <button
                                                        className="btn py-3 d-flex align-items-center justify-content-center w-100"
                                                        disabled
                                                        style={{ opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" }}
                                                      >
                                                        <img src="/assets/img/platform-icons/Google-Chat.png" alt="Google Chat" style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "12px" }} />
                                                        <span className="fw-medium fs-15">Google Chat</span>
                                                      </button>
                                                    </span>
                                                  </OverlayTrigger>
                                                ) : (
                                                  <button
                                                    className="btn btn-outline-primary py-3 d-flex align-items-center justify-content-center w-100"
                                                    onClick={() => {
                                                      setShowIntegrationSelectionModal(false);
                                                      handleGoogleChatConnect();
                                                    }}
                                                  >
                                                    <img src="/assets/img/platform-icons/Google-Chat.png" alt="Google Chat" style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "12px" }} />
                                                    <span className="fw-medium fs-15">Google Chat</span>
                                                  </button>
                                                )
                                              ) : (
                                                <>
                                                  <button
                                                    className="btn btn-outline-primary py-3 d-flex align-items-center justify-content-center w-100"
                                                    onClick={() => {
                                                      setShowIntegrationSelectionModal(false);
                                                      openAddChatbotModal();
                                                    }}
                                                  >
                                                    <img src="/assets/img/platform-icons/Engati.png" alt="Engati" style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "12px" }} />
                                                    <span className="fw-medium fs-15">Cloudee Chat Bot</span>
                                                  </button>

                                                  {/* <button
                                                    className="btn btn-outline-primary py-3 d-flex align-items-center justify-content-center w-100"
                                                    onClick={() => {
                                                      setShowIntegrationSelectionModal(false);
                                                      setActiveBrandIntegration("TikTok");
                                                    }}
                                                  >
                                                    <img src="/assets/img/platform-icons/TikTok.png" alt="TikTok" style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "12px" }} />
                                                    <span className="fw-medium fs-15">TikTok</span>
                                                  </button> */}


                                                  {/* <button
                                                    className="btn btn-outline-primary py-3 d-flex align-items-center justify-content-center w-100"
                                                    onClick={() => {
                                                      setShowIntegrationSelectionModal(false);
                                                      setActiveBrandIntegration("Shopee");
                                                    }}
                                                  >
                                                    <img src="/assets/img/platform-icons/Shopee.png" alt="Shopee" style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "12px" }} />
                                                    <span className="fw-medium fs-15">Shopee</span>
                                                  </button> */}

                                                  <button
                                                    className="btn btn-outline-primary py-3 d-flex align-items-center justify-content-center w-100"
                                                    onClick={() => {
                                                      setShowIntegrationSelectionModal(false);
                                                      setActiveBrandIntegration("Lazada");
                                                    }}
                                                  >
                                                    <img src="/assets/img/platform-icons/Lazada.png" alt="Lazada" style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "12px" }} />
                                                    <span className="fw-medium fs-15">Lazada</span>
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {activeBrandIntegration && (
                                    <div
                                      className="modal fade show d-block"
                                      tabIndex={-1}
                                      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                                      onClick={(e) => {
                                        if (e.target === e.currentTarget) setActiveBrandIntegration(null);
                                      }}
                                    >
                                      <div className="modal-dialog modal-dialog-centered">
                                        <div className="modal-content">
                                          <div className="modal-header border-0 pb-0">
                                            <button
                                              type="button"
                                              className="btn-close"
                                              onClick={() => setActiveBrandIntegration(null)}
                                            ></button>
                                          </div>
                                          <div className="modal-body text-center pt-0 pb-5 px-4">
                                            <div className="mb-4">
                                              <img src={`/assets/img/platform-icons/${activeBrandIntegration}.png`} alt={activeBrandIntegration} style={{ width: "100px", height: "100px", objectFit: "contain" }} className="mb-3" />
                                              <h4 className="mb-2 fw-bold">Connect your {activeBrandIntegration} Account</h4>
                                              <p className="text-muted">Integrate {activeBrandIntegration} to manage messages directly from our platform.</p>
                                            </div>
                                            <button
                                              className="btn btn-primary px-5 py-2 w-100 rounded-pill shadow-sm"
                                              onClick={() => handleBrandIntegrationConnect(activeBrandIntegration)}
                                              disabled={brandAuthLoading}
                                              style={{ fontSize: "1.1rem" }}
                                            >
                                              {brandAuthLoading ? (
                                                <>
                                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                  Connecting...
                                                </>
                                              ) : (
                                                <>
                                                  <i className="ti ti-plug-connected me-2"></i>
                                                  Connect {activeBrandIntegration}
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {showChatbotModal && (
                                    <div
                                      className="modal fade show d-block"
                                      tabIndex={-1}
                                      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                                      onClick={(e) => {
                                        if (e.target === e.currentTarget) closeChatbotModal();
                                      }}
                                    >
                                      <div className="modal-dialog modal-dialog-centered" style={{ minWidth: "50%" }}>
                                        <div className="modal-content">
                                          <div className="modal-header">
                                            <h5 className="modal-title">
                                              {editingChatbot ? "Edit Chatbot" : "Add Chatbot"}
                                            </h5>
                                            <button
                                              type="button"
                                              className="btn-close"
                                              onClick={closeChatbotModal}
                                            ></button>
                                          </div>
                                          <div className="modal-body">
                                            <div className="row">
                                              <div className="col-md-6 mb-3">
                                                <label className="form-label">
                                                  Bot Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                  type="text"
                                                  className="form-control"
                                                  placeholder="Enter Bot Name"
                                                  value={chatbotFormData.bot_name}
                                                  onChange={(e) =>
                                                    setChatbotFormData({
                                                      ...chatbotFormData,
                                                      bot_name: e.target.value,
                                                    })
                                                  }
                                                />
                                              </div>
                                              <div className="col-md-6 mb-3">
                                                <label className="form-label">
                                                  Bot ID <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                  type="text"
                                                  className="form-control"
                                                  placeholder="Enter Bot ID"
                                                  value={chatbotFormData.bot_id}
                                                  onChange={(e) =>
                                                    setChatbotFormData({
                                                      ...chatbotFormData,
                                                      bot_id: e.target.value,
                                                    })
                                                  }
                                                />
                                              </div>
                                            </div>
                                            {userRole?.role_key !== "SALESUSER" && (
                                              <div className="row">
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label">
                                                    Bot Key <span className="text-danger">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot Key"
                                                    value={chatbotFormData.bot_key}
                                                    onChange={(e) =>
                                                      setChatbotFormData({
                                                        ...chatbotFormData,
                                                        bot_key: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label">
                                                    Webhook URL <span className="text-danger">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Webhook URL"
                                                    value={chatbotFormData.bot_message_webhook_url}
                                                    onChange={(e) =>
                                                      setChatbotFormData({
                                                        ...chatbotFormData,
                                                        bot_message_webhook_url: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            )}
                                            {userRole?.role_key !== "SALESUSER" && (
                                              <div className="row">
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label">
                                                    Bot Customer ID <span className="text-danger">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot Customer ID"
                                                    value={chatbotFormData.bot_customer_id}
                                                    onChange={(e) =>
                                                      setChatbotFormData({
                                                        ...chatbotFormData,
                                                        bot_customer_id: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label">
                                                    Bot API Key <span className="text-danger">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot API Key"
                                                    value={chatbotFormData.bot_api_key}
                                                    onChange={(e) =>
                                                      setChatbotFormData({
                                                        ...chatbotFormData,
                                                        bot_api_key: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            )}
                                            {userRole?.role_key !== "SALESUSER" && (
                                              <div className="row">
                                                <div className="col-md-6 mb-3">
                                                  <label className="form-label d-flex align-items-center">
                                                    Bot Flow Key
                                                    <OverlayTrigger
                                                      placement="top"
                                                      overlay={
                                                        <Tooltip id="bot-flow-key-tooltip">
                                                          Enter the Flow Key of the path that triggers the <strong>Transfer to Agent</strong> node in your Engati bot flow to enable CRM to trigger chat handoff from the bot to a real agent.
                                                        </Tooltip>
                                                      }
                                                    >
                                                      <i className="fa fa-info-circle ms-2 text-muted" style={{ cursor: "pointer" }}></i>
                                                    </OverlayTrigger>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Bot Flow Key"
                                                    value={chatbotFormData.bot_flow_key}
                                                    onChange={(e) =>
                                                      setChatbotFormData({
                                                        ...chatbotFormData,
                                                        bot_flow_key: e.target.value,
                                                      })
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="modal-footer">
                                            <button
                                              type="button"
                                              className="btn btn-secondary"
                                              onClick={closeChatbotModal}
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-primary"
                                              onClick={handleSaveChatbot}
                                              disabled={chatbotLoading}
                                            >
                                              <i className="fa fa-plus me-1"></i>
                                              {chatbotLoading
                                                ? "Saving..."
                                                : editingChatbot
                                                  ? "Update"
                                                  : "Add"}

                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              </div>
                            )}

                            {chatSettingsTab === "status" && (
                              <div className="tab-pane fade show active">
                                {chatStatusLoading ? (
                                  <div
                                    className="d-flex justify-content-center align-items-center"
                                    style={{ minHeight: "200px" }}
                                  >
                                    <div className="spinner-border text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="border-bottom mb-3 pb-3">
                                      <h4 className="fw-semibold mb-1">Chat Status Settings</h4>
                                      <p>Configure the chat status options below</p>
                                    </div>

                                    <div className="mb-3">
                                      <label className="form-label fw-medium">Status Options</label>
                                    </div>
                                    {chatStatuses
                                      .slice()
                                      .sort((a, b) => a.order - b.order)
                                      .map((status, idx) => (
                                        <div
                                          key={status.id}
                                          className="d-flex align-items-center mb-2 p-2 border rounded"
                                        >
                                          <input
                                            type="text"
                                            className="form-control me-3"
                                            placeholder="Status name"
                                            value={status.name}
                                            onChange={(e) => updateChatStatusName(idx, e.target.value)}
                                            disabled={idx === 0 || idx === chatStatuses.length - 1}
                                          />
                                          <>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-secondary me-1"
                                              onClick={(e) => {
                                                moveChatStatus(idx, -1);
                                                e.currentTarget.blur();
                                              }}
                                              disabled={idx <= 1 || idx === chatStatuses.length - 1}
                                            >
                                              ▲
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-secondary me-3"
                                              onClick={(e) => {
                                                moveChatStatus(idx, 1);
                                                e.currentTarget.blur();
                                              }}
                                              disabled={idx === 0 || idx >= chatStatuses.length - 2}
                                            >
                                              ▼
                                            </button>
                                            <button
                                              className="btn btn-sm btn-danger"
                                              onClick={(e) => {
                                                removeChatStatus(idx);
                                                e.currentTarget.blur();
                                              }}
                                              disabled={idx === 0 || idx === chatStatuses.length - 1}
                                            >
                                              ✕
                                            </button>
                                          </>
                                        </div>
                                      ))}

                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm mt-2 mb-4"
                                      onClick={(e) => {
                                        addChatStatus();
                                        e.currentTarget.blur();
                                      }}
                                    >
                                      <i className="fa-solid fa-plus me-1" /> Add Status
                                    </button>

                                    <div>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary me-2"
                                        onClick={(e) => {
                                          cancelChatStatusChanges();
                                          e.currentTarget.blur();
                                        }}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-primary me-2"
                                        onClick={(e) => {
                                          saveChatStatuses();
                                          e.currentTarget.blur();
                                        }}
                                      >
                                        Save Changes
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}

                            {chatSettingsTab === "privacy" && (
                              <div className="tab-pane fade show active">
                                <PrivacySettings />
                              </div>
                            )}

                            {chatSettingsTab === "team" && (
                              <div className="tab-pane fade show active">
                                <TeamAllocationSettings />
                              </div>
                            )}

                            {chatSettingsTab === "template" && (
                              <div className="tab-pane fade show active">
                                {templateLoading ? (
                                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                                    <div className="spinner-border text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </div>
                                ) : selectedCategory ? (
                                  <>
                                    {/* Messages View - Shows when a category is selected */}
                                    <div className="d-flex align-items-center mb-4">
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary me-3"
                                        onClick={() => {
                                          setSelectedCategory(null);
                                          setTemplateMessages([]);
                                        }}
                                        style={{ borderRadius: "50%", width: "20px", height: "20px", padding: 0 }}
                                      >
                                        <i className="fa-solid fa-arrow-left" style={{ fontSize: "1rem" }}></i>
                                      </button>
                                      <div className="flex-grow-1">
                                        <h5 className="fw-semibold mb-0">
                                          <i className="ti ti-folder-open text-primary me-2"></i>
                                          {selectedCategory.category_name}
                                        </h5>
                                        {selectedCategory.description && (
                                          <small className="text-muted">{selectedCategory.description}</small>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                          setEditingMessage(null);
                                          setMessageFormData({ title: "", short_code: "", message: "" });
                                          setShowMessageModal(true);
                                        }}
                                      >
                                        <i className="fa-solid fa-plus me-1"></i> Add Template
                                      </button>
                                    </div>

                                    {templateMessages.length === 0 ? (
                                      <div className="text-center py-5">
                                        <i className="ti ti-message-off" style={{ fontSize: "48px", color: "#ccc" }}></i>
                                        <p className="text-muted mt-3">No templates in this category yet.</p>
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary"
                                          onClick={() => {
                                            setEditingMessage(null);
                                            setMessageFormData({ title: "", short_code: "", message: "" });
                                            setShowMessageModal(true);
                                          }}
                                        >
                                          Create Your First Template
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="row g-3">
                                        {templateMessages.map((msg) => (
                                          <div key={msg.chat_template_message_id} className="col-md-6 col-lg-4">
                                            <div
                                              className="card h-100"
                                              style={{
                                                cursor: "pointer",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                border: "1px solid #e9ecef",
                                                transition: "all 0.3s ease",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-4px)";
                                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                                              }}
                                              onClick={() => openEditMessageModal(msg)}
                                            >
                                              <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                  <h6 className="card-title mb-0 fw-semibold">{msg.title}</h6>
                                                  <span className="badge bg-primary">{msg.short_code}</span>
                                                </div>
                                                <p
                                                  className="card-text text-muted small mb-0"
                                                  style={{
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    whiteSpace: "pre-wrap",
                                                  }}
                                                >
                                                  {msg.message.length > 20 ? msg.message.substring(0, 20) + "..." : msg.message}
                                                </p>
                                              </div>
                                              <div className="card-footer bg-transparent border-top-0 pt-0">
                                                <div className="d-flex justify-content-end gap-2">
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      openEditMessageModal(msg);
                                                    }}
                                                  >
                                                    <i className="ti ti-edit"></i>
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteMessage(msg);
                                                    }}
                                                  >
                                                    <i className="ti ti-trash"></i>
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {/* Categories View - Shows folder cards */}
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                      <div>
                                        <h5 className="fw-semibold mb-1">Chat Templates</h5>
                                        <p className="text-muted mb-0">Click on a category to view and manage messages</p>
                                      </div>
                                      <button type="button" className="btn btn-primary" onClick={() => setShowCategoryModal(true)}>
                                        <i className="ti ti-folder-plus me-1"></i> Add Category
                                      </button>
                                    </div>

                                    {templateCategories.length === 0 ? (
                                      <div className="text-center py-5">
                                        <i className="ti ti-folder-off" style={{ fontSize: "48px", color: "#ccc" }}></i>
                                        <p className="text-muted mt-3">No template categories created yet.</p>
                                        <button type="button" className="btn btn-outline-primary" onClick={() => setShowCategoryModal(true)}>
                                          Create Your First Category
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="row g-3">
                                        {templateCategories.map((cat) => (
                                          <div key={cat.chat_template_category_id} className="col-md-4 col-lg-3">
                                            <div
                                              className="card text-center h-100"
                                              style={{
                                                cursor: "pointer",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                border: "1px solid #e9ecef",
                                                transition: "all 0.3s ease",
                                                position: "relative",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
                                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0) scale(1)";
                                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                                              }}
                                              onClick={() => {
                                                setSelectedCategory(cat);
                                                fetchTemplateMessages(cat.chat_template_category_id);
                                              }}
                                            >
                                              {/* 3-dot menu at top-right */}
                                              <div style={{ position: "absolute", top: "8px", right: "8px" }}>
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-light"
                                                  style={{ borderRadius: "50%", width: "28px", height: "28px", padding: 0 }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenCategoryMenuId(openCategoryMenuId === cat.chat_template_category_id ? null : cat.chat_template_category_id);
                                                  }}
                                                >
                                                  <i className="ti ti-dots-vertical" style={{ fontSize: "14px" }}></i>
                                                </button>
                                                {openCategoryMenuId === cat.chat_template_category_id && (
                                                  <div
                                                    style={{
                                                      position: "absolute",
                                                      top: "100%",
                                                      right: 0,
                                                      minWidth: "120px",
                                                      backgroundColor: "#fff",
                                                      border: "1px solid #e9ecef",
                                                      borderRadius: "8px",
                                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                                      zIndex: 1000,
                                                      marginTop: "4px",
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm w-100 text-start text-primary"
                                                      style={{ borderRadius: "8px 8px 0 0", padding: "4px 8px", marginTop: "4px" }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenCategoryMenuId(null);
                                                        setEditingCategory(cat);
                                                        setCategoryFormData({
                                                          category_name: cat.category_name,
                                                          description: cat.description || "",
                                                        });
                                                        setShowCategoryModal(true);
                                                      }}
                                                    >
                                                      <i className="ti ti-edit me-2"></i> Edit
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm w-100 text-start text-danger"
                                                      style={{ borderRadius: "0 0 8px 8px", padding: "4px 8px", marginBottom: "4px" }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenCategoryMenuId(null);
                                                        handleDeleteCategory(cat);
                                                      }}
                                                    >
                                                      <i className="ti ti-trash me-2"></i> Delete
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="card-body py-4">
                                                <div
                                                  className="mb-3"
                                                  style={{
                                                    width: "64px",
                                                    height: "64px",
                                                    borderRadius: "16px",
                                                    backgroundColor: "#e8f4fd",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: "0 auto",
                                                  }}
                                                >
                                                  <i className="ti ti-template text-primary" style={{ fontSize: "32px" }}></i>
                                                </div>
                                                <h6 className="card-title fw-semibold mb-1">{cat.category_name}</h6>
                                                {cat.description && (
                                                  <small className="text-muted d-block" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {cat.description.length > 30 ? cat.description.substring(0, 30) + "..." : cat.description}
                                                  </small>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}

                                {showCategoryModal && (
                                  <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={(e) => { if (e.target === e.currentTarget) { setShowCategoryModal(false); setCategoryFormData({ category_name: "", description: "" }); setEditingCategory(null); } }}>
                                    <div className="modal-dialog modal-dialog-centered">
                                      <div className="modal-content">
                                        <div className="modal-header">
                                          <h5 className="modal-title">{editingCategory ? "Edit Category" : "Add Category"}</h5>
                                          <button type="button" className="btn-close" onClick={() => { setShowCategoryModal(false); setCategoryFormData({ category_name: "", description: "" }); setEditingCategory(null); }}></button>
                                        </div>
                                        <div className="modal-body">
                                          <div className="mb-3">
                                            <label className="form-label">Category Name <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" placeholder="e.g., Promotions" value={categoryFormData.category_name} onChange={(e) => setCategoryFormData({ ...categoryFormData, category_name: e.target.value })} />
                                          </div>
                                          <div className="mb-3">
                                            <label className="form-label">Description</label>
                                            <textarea className="form-control" rows={3} placeholder="Optional description" value={categoryFormData.description} onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}></textarea>
                                          </div>
                                        </div>
                                        <div className="modal-footer">
                                          <button type="button" className="btn btn-secondary" onClick={() => { setShowCategoryModal(false); setCategoryFormData({ category_name: "", description: "" }); setEditingCategory(null); }}>Cancel</button>
                                          <button type="button" className="btn btn-primary" onClick={handleSaveCategory} disabled={templateLoading}>{templateLoading ? (editingCategory ? "Saving..." : "Creating...") : (editingCategory ? "Save Changes" : "Create Category")}</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {showMessageModal && (
                                  <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={(e) => { if (e.target === e.currentTarget) { setShowMessageModal(false); setMessageFormData({ title: "", short_code: "", message: "" }); setEditingMessage(null); } }}>
                                    <div className="modal-dialog modal-dialog-centered modal-lg">
                                      <div className="modal-content">
                                        <div className="modal-header">
                                          <h5 className="modal-title">{editingMessage ? "Edit Template Message" : "Add Template Message"}</h5>
                                          <button type="button" className="btn-close" onClick={() => { setShowMessageModal(false); setMessageFormData({ title: "", short_code: "", message: "" }); setEditingMessage(null); }}></button>
                                        </div>
                                        <div className="modal-body">
                                          <div className="row">
                                            <div className="col-md-6 mb-3">
                                              <label className="form-label">Title <span className="text-danger">*</span></label>
                                              <input type="text" className="form-control" placeholder="e.g., Welcome Message" value={messageFormData.title} onChange={(e) => setMessageFormData({ ...messageFormData, title: e.target.value })} />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                              <label className="form-label">Short Code <span className="text-danger">*</span></label>
                                              <div style={{ position: "relative" }}>
                                                <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#6c757d", fontWeight: "500", pointerEvents: "none" }}>/</span>
                                                <input type="text" className="form-control" style={{ paddingLeft: "24px" }} placeholder="Enter Short Code" value={messageFormData.short_code.replace(/^\//, "")} onChange={(e) => setMessageFormData({ ...messageFormData, short_code: e.target.value.replace(/\//g, "") })} />
                                              </div>
                                            </div>
                                          </div>
                                          <div className="mb-3" style={{ position: "relative" }}>
                                            <label className="form-label">Message <span className="text-danger">*</span></label>
                                            <textarea
                                              ref={messageTextareaRef}
                                              className="form-control"
                                              rows={4}
                                              placeholder="Type your message here... (use {{ to insert placeholders)"
                                              value={messageFormData.message}
                                              onChange={handleMessageChange}
                                              onBlur={() => setTimeout(() => setShowPlaceholderDropdown(false), 200)}
                                            ></textarea>
                                            {showPlaceholderDropdown && filteredPlaceholders.length > 0 && (
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  bottom: "100%",
                                                  left: 0,
                                                  right: 0,
                                                  maxHeight: "180px",
                                                  overflowY: "auto",
                                                  backgroundColor: "#fff",
                                                  border: "1px solid #e9ecef",
                                                  borderRadius: "8px",
                                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                                  zIndex: 1000,
                                                  marginBottom: "4px",
                                                }}
                                              >
                                                {filteredPlaceholders.map((ph) => (
                                                  <div
                                                    key={ph.chat_template_placeholder_id}
                                                    style={{
                                                      padding: "10px 14px",
                                                      cursor: "pointer",
                                                      borderBottom: "1px solid #f3f4f6",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                                                    onMouseDown={(e) => {
                                                      e.preventDefault();
                                                      insertPlaceholder(ph.chat_template_placeholder_key);
                                                    }}
                                                  >
                                                    <div className="d-flex align-items-center">
                                                      <span>{ph.chat_template_placeholder_display_name}</span>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            <small className="text-muted">Type <code>{"{{"}</code> to see available placeholders</small>
                                          </div>
                                        </div>
                                        <div className="modal-footer">
                                          <button type="button" className="btn btn-secondary" onClick={() => { setShowMessageModal(false); setMessageFormData({ title: "", short_code: "", message: "" }); setEditingMessage(null); }}>Cancel</button>
                                          <button type="button" className="btn btn-primary" onClick={handleSaveMessage} disabled={templateLoading}>{templateLoading ? (editingMessage ? "Saving..." : "Creating...") : (editingMessage ? "Save Changes" : "Create Message")}</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {chatSettingsTab === "autoreply" && (
                              <div className="tab-pane fade show active">
                                {autoReplyLoading ? (
                                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                                    <div className="spinner-border text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="border-bottom mb-4 pb-3 d-flex justify-content-between align-items-start">
                                      <div>
                                        <h5 className="fw-semibold mb-1">
                                          <i className="ti ti-clock-hour-4 text-primary me-2"></i>
                                          Business Hours
                                        </h5>
                                        <p className="text-muted mb-0">Set your business hours for each day of the week. Auto-reply messages will be sent outside these hours.</p>
                                      </div>
                                      <div style={{ minWidth: "250px" }}>
                                        <label className="form-label small fw-medium text-muted mb-1">Timezone</label>
                                        <Select
                                          classNamePrefix="react-select"
                                          options={(Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone').map((tz: string) => ({ value: tz, label: tz })) : [{ value: Intl.DateTimeFormat().resolvedOptions().timeZone, label: Intl.DateTimeFormat().resolvedOptions().timeZone }]}
                                          value={{ value: selectedTimezone, label: selectedTimezone }}
                                          onChange={(opt: any) => opt && handleTimezoneChange(opt.value)}
                                          styles={{
                                            ...customSelectStyles,
                                            menu: (base: any) => ({ ...base, zIndex: 9999 })
                                          }}
                                          isSearchable
                                        />
                                      </div>
                                    </div>

                                    <div className="row g-3 mb-4">
                                      {weekDays.map((day) => {
                                        const businessHour = businessHours.find((bh) => bh.week_day_id === day.week_day_id);
                                        const isEnabled = !!businessHour;
                                        return (
                                          <div key={day.week_day_id} className="col-12">
                                            <div
                                              className="d-flex align-items-center p-3 rounded"
                                              style={{
                                                backgroundColor: isEnabled ? "#f0f9ff" : "#f8f9fa",
                                                border: isEnabled ? "1px solid #0ea5e9" : "1px solid #e9ecef",
                                                transition: "all 0.2s ease",
                                              }}
                                            >
                                              <div className="form-check me-3" style={{ minWidth: "150px" }}>
                                                <input
                                                  type="checkbox"
                                                  className="form-check-input"
                                                  id={`day-${day.week_day_id}`}
                                                  checked={isEnabled}
                                                  onChange={() => toggleBusinessHourDay(day.week_day_id)}
                                                  style={{ width: "20px", height: "20px" }}
                                                />
                                                <label
                                                  className="form-check-label fw-medium ms-2"
                                                  htmlFor={`day-${day.week_day_id}`}
                                                  style={{ fontSize: "1rem" }}
                                                >
                                                  {day.week_day_display_name}
                                                </label>
                                              </div>
                                              {isEnabled && (
                                                <div className="d-flex align-items-center gap-2 flex-grow-1">
                                                  <div className="d-flex align-items-center gap-2">
                                                    <label className="text-muted small mb-0">From:</label>
                                                    <input
                                                      type="time"
                                                      className="form-control form-control-sm"
                                                      style={{ width: "130px" }}
                                                      value={businessHour.start_time.substring(0, 5)}
                                                      onChange={(e) => updateBusinessHourTime(day.week_day_id, "start_time", e.target.value)}
                                                    />
                                                  </div>
                                                  <div className="d-flex align-items-center gap-2">
                                                    <label className="text-muted small mb-0">To:</label>
                                                    <input
                                                      type="time"
                                                      className="form-control form-control-sm"
                                                      style={{ width: "130px" }}
                                                      value={businessHour.end_time.substring(0, 5)}
                                                      onChange={(e) => updateBusinessHourTime(day.week_day_id, "end_time", e.target.value)}
                                                    />
                                                  </div>
                                                </div>
                                              )}
                                              {!isEnabled && (
                                                <span className="text-muted small">Closed</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    <div className="d-flex justify-content-end mb-4 border-bottom pb-3" style={!hasBusinessHoursChanged() ? { minHeight: "24px" } : {}}>
                                      {hasBusinessHoursChanged() && (
                                        <button type="button" className="btn btn-primary" onClick={saveBusinessHours} disabled={autoReplyLoading}>
                                          <i className="ti ti-device-floppy me-1"></i>
                                          Save Business Hours
                                        </button>
                                      )}
                                    </div>

                                    <div className="border-bottom mb-4 pb-3">
                                      <h5 className="fw-semibold mb-1">
                                        <i className="ti ti-message-2 text-primary me-2"></i>
                                        Auto-Reply Messages
                                      </h5>
                                      <p className="text-muted mb-0">Configure automatic reply messages that will be sent outside business hours.</p>
                                    </div>

                                    {autoReplySettings.length === 0 && !showNewAutoReplyForm && (
                                      <div className="text-center py-5">
                                        <i className="ti ti-message-off" style={{ fontSize: "48px", color: "#ccc" }}></i>
                                        <p className="text-muted mt-3">No auto-reply messages configured yet.</p>
                                        <button type="button" className="btn btn-outline-primary" onClick={addAutoReplySetting}>
                                          <i className="fa fa-plus me-1"></i>
                                          Add Auto-Reply Message
                                        </button>
                                      </div>
                                    )}

                                    {autoReplySettings.length > 0 && (
                                      <>
                                        <div className="auto-reply-channel-sections">
                                          {getGroupedAutoReplySettings().map((group) => {
                                            const isExpanded = expandedAutoReplyChannels.has(group.channelKey);
                                            const channelKey = group.channelKey;

                                            return (
                                              <div
                                                key={group.channelKey}
                                                className="border rounded mb-3"
                                                style={{
                                                  overflow: "hidden",
                                                  transition: "all 0.2s ease",
                                                }}
                                              >
                                                <div
                                                  className="d-flex align-items-center justify-content-between px-3 py-3"
                                                  style={{
                                                    backgroundColor: getChannelBgColor(channelKey),
                                                    cursor: "pointer",
                                                    transition: "background-color 0.2s ease",
                                                  }}
                                                  onClick={() => toggleAutoReplyChannel(group.channelKey)}
                                                >
                                                  <div className="d-flex align-items-center">
                                                    <i
                                                      className={channelKey === "GLOBAL" ? "ti ti-world" : getChannelIcon(channelKey)}
                                                      style={{
                                                        color: getChannelColor(channelKey),
                                                        fontSize: "22px",
                                                        marginRight: "12px",
                                                      }}
                                                    ></i>
                                                    <div>
                                                      <h6 className="mb-0 fw-semibold">
                                                        {group.channelName}
                                                      </h6>
                                                      <small className="text-muted">
                                                        {group.settings.length} message{group.settings.length !== 1 ? "s" : ""}
                                                      </small>
                                                    </div>
                                                  </div>
                                                  <div className="d-flex align-items-center">
                                                    <span
                                                      className="badge me-3"
                                                      style={{
                                                        backgroundColor: getChannelColor(channelKey),
                                                        color: "#fff",
                                                      }}
                                                    >
                                                      {group.settings.length}
                                                    </span>
                                                    <i
                                                      className={`ti ti-chevron-${isExpanded ? "up" : "down"}`}
                                                      style={{
                                                        fontSize: "18px",
                                                        color: getChannelColor(channelKey),
                                                        transition: "transform 0.2s ease",
                                                      }}
                                                    ></i>
                                                  </div>
                                                </div>

                                                {isExpanded && (
                                                  <div
                                                    className="px-3 py-3"
                                                    style={{
                                                      backgroundColor: "#fafafa",
                                                      borderTop: `1px solid ${getChannelColor(channelKey)}20`,
                                                    }}
                                                  >
                                                    {group.settings.map(({ setting, originalIndex }) => (
                                                      <div
                                                        key={setting.tenant_chat_auto_reply_setting_id || `new-${originalIndex}`}
                                                        className="card mb-3"
                                                        style={{
                                                          border: "1px solid #e9ecef",
                                                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                                        }}
                                                      >
                                                        <div className="card-body">
                                                          <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div className="d-flex align-items-center gap-3">
                                                              <div className="form-check form-switch">
                                                                <input
                                                                  type="checkbox"
                                                                  className="form-check-input"
                                                                  id={`active-${originalIndex}`}
                                                                  checked={setting.is_active}
                                                                  onChange={(e) => updateAutoReplySetting(originalIndex, "is_active", e.target.checked)}
                                                                  style={{ width: "40px", height: "20px" }}
                                                                />
                                                                <label className="form-check-label small text-muted ms-1" htmlFor={`active-${originalIndex}`}>
                                                                  {setting.is_active ? "Active" : "Inactive"}
                                                                </label>
                                                              </div>
                                                              <div className="form-check">
                                                                <input
                                                                  type="checkbox"
                                                                  className="form-check-input"
                                                                  id={`global-${originalIndex}`}
                                                                  checked={setting.is_global}
                                                                  onChange={(e) => updateAutoReplySetting(originalIndex, "is_global", e.target.checked)}
                                                                />
                                                                <label className="form-check-label small" htmlFor={`global-${originalIndex}`}>
                                                                  Apply to all channels
                                                                </label>
                                                              </div>
                                                            </div>
                                                            <button
                                                              type="button"
                                                              className="btn btn-sm btn-outline-danger"
                                                              onClick={() => removeAutoReplySetting(originalIndex)}
                                                            >
                                                              <i className="ti ti-trash"></i>
                                                            </button>
                                                          </div>

                                                          {!setting.is_global && (
                                                            <div className="mb-3">
                                                              <label className="form-label small fw-medium">Select Account</label>
                                                              <Select
                                                                classNamePrefix="react-select"
                                                                options={channelOptions}
                                                                value={channelOptions.find(opt => opt.value === setting.external_chat_bot_channel_account_id)}
                                                                onChange={(opt: any) => updateAutoReplySetting(originalIndex, "external_chat_bot_channel_account_id", opt ? opt.value : null)}
                                                                formatOptionLabel={formatChannelOption}
                                                                styles={customSelectStyles}
                                                                placeholder="Choose a channel..."
                                                                isClearable
                                                              />
                                                            </div>
                                                          )}

                                                          <div className="mb-0" style={{ position: "relative" }}>
                                                            <label className="form-label small fw-medium">Auto-Reply Message</label>
                                                            {activeTemplateDropdownIndex === originalIndex && filteredAutoReplyTemplates.length > 0 && (
                                                              <div
                                                                style={{
                                                                  position: "absolute",
                                                                  bottom: "100%",
                                                                  left: 0,
                                                                  right: 0,
                                                                  maxHeight: "200px",
                                                                  overflowY: "auto",
                                                                  background: "#fff",
                                                                  border: "1px solid #e5e7eb",
                                                                  borderRadius: "8px",
                                                                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                                                  zIndex: 1000,
                                                                  marginBottom: "8px",
                                                                }}
                                                              >
                                                                {filteredAutoReplyTemplates.map((template) => (
                                                                  <div
                                                                    key={template.chat_template_message_id}
                                                                    style={{
                                                                      padding: "10px 14px",
                                                                      cursor: "pointer",
                                                                      borderBottom: "1px solid #f3f4f6",
                                                                    }}
                                                                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                                                                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                                                                    onMouseDown={(e) => {
                                                                      e.preventDefault();
                                                                      const currentMessage = setting.auto_reply_message;
                                                                      const lastSlashIndex = currentMessage.lastIndexOf("/");
                                                                      if (lastSlashIndex !== -1) {
                                                                        const newMessage = currentMessage.substring(0, lastSlashIndex) + template.message;
                                                                        updateAutoReplySetting(originalIndex, "auto_reply_message", newMessage);
                                                                      } else {
                                                                        updateAutoReplySetting(originalIndex, "auto_reply_message", currentMessage + template.message);
                                                                      }
                                                                      setActiveTemplateDropdownIndex(null);
                                                                    }}
                                                                  >
                                                                    <div className="d-flex align-items-center">
                                                                      <span className="badge bg-primary me-2">{template.short_code}</span>
                                                                      <strong>{template.title}</strong>
                                                                    </div>
                                                                    <small className="text-muted" style={{ display: "block", marginTop: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                      {template.message.substring(0, 60)}{template.message.length > 60 ? "..." : ""}
                                                                    </small>
                                                                  </div>
                                                                ))}
                                                              </div>
                                                            )}
                                                            <textarea
                                                              className="form-control"
                                                              rows={3}
                                                              placeholder="Type / to use templates..."
                                                              value={setting.auto_reply_message}
                                                              onChange={(e) => {
                                                                const value = e.target.value;
                                                                updateAutoReplySetting(originalIndex, "auto_reply_message", value);

                                                                const lastSlashIndex = value.lastIndexOf("/");
                                                                if (lastSlashIndex !== -1) {
                                                                  const searchTerm = value.substring(lastSlashIndex).toLowerCase();
                                                                  const filtered = autoReplyTemplates.filter(
                                                                    (t) => t.short_code.toLowerCase().includes(searchTerm) || t.title.toLowerCase().includes(searchTerm.slice(1))
                                                                  );
                                                                  if (filtered.length > 0) {
                                                                    setFilteredAutoReplyTemplates(filtered);
                                                                    setActiveTemplateDropdownIndex(originalIndex);
                                                                  } else {
                                                                    setActiveTemplateDropdownIndex(null);
                                                                  }
                                                                } else {
                                                                  setActiveTemplateDropdownIndex(null);
                                                                }
                                                              }}
                                                              onBlur={() => {
                                                                setTimeout(() => setActiveTemplateDropdownIndex(null), 150);
                                                              }}
                                                            ></textarea>
                                                            <small className="text-muted">Type <code>/</code> to see available templates</small>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ))}

                                                    <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                                                      <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => cancelChannelAutoReplyChanges(group.channelKey)}
                                                      >
                                                        Cancel
                                                      </button>
                                                      <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => saveChannelAutoReplySettings(group.channelKey)}
                                                        disabled={autoReplyLoading}
                                                      >
                                                        <i className="ti ti-device-floppy me-1"></i>
                                                        Save {group.channelName}
                                                      </button>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>

                                        <div className="d-flex justify-content-start mt-4 mb-4">
                                          <button type="button" className="btn btn-outline-primary" onClick={addAutoReplySetting} disabled={showNewAutoReplyForm}>
                                            <i className="fa fa-plus me-1"></i>
                                            Add Another Message
                                          </button>
                                        </div>
                                      </>
                                    )}

                                    {showNewAutoReplyForm && (
                                      <div
                                        className="card mb-4"
                                        style={{
                                          border: "2px solid #6366f1",
                                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                                        }}
                                      >
                                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                          <h6 className="mb-0 fw-semibold">
                                            <i className="fa fa-plus me-2 text-primary"></i>
                                            New Auto-Reply Message
                                          </h6>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={cancelNewAutoReplySetting}
                                          >
                                            <i className="ti ti-x"></i>
                                          </button>
                                        </div>
                                        <div className="card-body">
                                          <div className="d-flex align-items-center gap-3 mb-3">
                                            <div className="form-check form-switch">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="new-active"
                                                checked={newAutoReplySetting.is_active}
                                                onChange={(e) => setNewAutoReplySetting({ ...newAutoReplySetting, is_active: e.target.checked })}
                                                style={{ width: "40px", height: "20px" }}
                                              />
                                              <label className="form-check-label small text-muted ms-1" htmlFor="new-active">
                                                {newAutoReplySetting.is_active ? "Active" : "Inactive"}
                                              </label>
                                            </div>
                                            <div className="form-check">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="new-global"
                                                checked={newAutoReplySetting.is_global}
                                                onChange={(e) => setNewAutoReplySetting({ ...newAutoReplySetting, is_global: e.target.checked, external_chat_bot_channel_account_id: e.target.checked ? null : newAutoReplySetting.external_chat_bot_channel_account_id })}
                                              />
                                              <label className="form-check-label small" htmlFor="new-global">
                                                Apply to all channels
                                              </label>
                                            </div>
                                          </div>

                                          {!newAutoReplySetting.is_global && (
                                            <div className="mb-3">
                                              <label className="form-label small fw-medium">Select Account <span className="text-danger">*</span></label>
                                              <select
                                                className="form-select"
                                                value={newAutoReplySetting.external_chat_bot_channel_account_id || ""}
                                                onChange={(e) => setNewAutoReplySetting({ ...newAutoReplySetting, external_chat_bot_channel_account_id: e.target.value ? Number(e.target.value) : null })}
                                              >
                                                <option value="">Choose a channel...</option>
                                                {channels.map((channel) => (
                                                  <option key={channel.external_chat_bot_channel_account_id} value={channel.external_chat_bot_channel_account_id}>
                                                    {channel.account_name}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          )}

                                          <div className="mb-3" style={{ position: "relative" }}>
                                            <label className="form-label small fw-medium">Auto-Reply Message <span className="text-danger">*</span></label>
                                            {activeTemplateDropdownIndex === -1 && filteredAutoReplyTemplates.length > 0 && (
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  bottom: "100%",
                                                  left: 0,
                                                  right: 0,
                                                  maxHeight: "200px",
                                                  overflowY: "auto",
                                                  background: "#fff",
                                                  border: "1px solid #e5e7eb",
                                                  borderRadius: "8px",
                                                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                                  zIndex: 1000,
                                                  marginBottom: "8px",
                                                }}
                                              >
                                                {filteredAutoReplyTemplates.map((template) => (
                                                  <div
                                                    key={template.chat_template_message_id}
                                                    style={{
                                                      padding: "10px 14px",
                                                      cursor: "pointer",
                                                      borderBottom: "1px solid #f3f4f6",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                                                    onMouseDown={(e) => {
                                                      e.preventDefault();
                                                      const currentMessage = newAutoReplySetting.auto_reply_message;
                                                      const lastSlashIndex = currentMessage.lastIndexOf("/");
                                                      if (lastSlashIndex !== -1) {
                                                        const newMessage = currentMessage.substring(0, lastSlashIndex) + template.message;
                                                        setNewAutoReplySetting({ ...newAutoReplySetting, auto_reply_message: newMessage });
                                                      } else {
                                                        setNewAutoReplySetting({ ...newAutoReplySetting, auto_reply_message: currentMessage + template.message });
                                                      }
                                                      setActiveTemplateDropdownIndex(null);
                                                    }}
                                                  >
                                                    <div className="d-flex align-items-center">
                                                      <span className="badge bg-primary me-2">{template.short_code}</span>
                                                      <strong>{template.title}</strong>
                                                    </div>
                                                    <small className="text-muted" style={{ display: "block", marginTop: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                      {template.message.substring(0, 60)}{template.message.length > 60 ? "..." : ""}
                                                    </small>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            <textarea
                                              className="form-control"
                                              rows={3}
                                              placeholder="Type / to use templates..."
                                              value={newAutoReplySetting.auto_reply_message}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                setNewAutoReplySetting({ ...newAutoReplySetting, auto_reply_message: value });

                                                const lastSlashIndex = value.lastIndexOf("/");
                                                if (lastSlashIndex !== -1) {
                                                  const searchTerm = value.substring(lastSlashIndex).toLowerCase();
                                                  const filtered = autoReplyTemplates.filter(
                                                    (t) => t.short_code.toLowerCase().includes(searchTerm) || t.title.toLowerCase().includes(searchTerm.slice(1))
                                                  );
                                                  if (filtered.length > 0) {
                                                    setFilteredAutoReplyTemplates(filtered);
                                                    setActiveTemplateDropdownIndex(-1);
                                                  } else {
                                                    setActiveTemplateDropdownIndex(null);
                                                  }
                                                } else {
                                                  setActiveTemplateDropdownIndex(null);
                                                }
                                              }}
                                              onBlur={() => {
                                                setTimeout(() => setActiveTemplateDropdownIndex(null), 150);
                                              }}
                                            ></textarea>
                                            <small className="text-muted">Type <code>/</code> to see available templates</small>
                                          </div>

                                          <div className="d-flex justify-content-end gap-2">
                                            <button type="button" className="btn btn-outline-secondary" onClick={cancelNewAutoReplySetting}>
                                              Cancel
                                            </button>
                                            <button type="button" className="btn btn-primary" onClick={confirmNewAutoReplySetting}>
                                              <i className="ti ti-check me-1"></i>
                                              Add Message
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* /Settings Info */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSettings;